/**
 * ============================================================================
 *  Instavc SDK — ONE file: chat + presence + Firebase push for the centralized
 *  signaling server. Framework-agnostic (no React / store / app imports).
 * ============================================================================
 *
 *  Everything a client needs to talk to the Instavc signaling + storage +
 *  push stack lives here. Drop this single file into any TS/JS project. One
 *  hard peer dependency: `phoenix` (the JS Channels client). `firebase` is an
 *  OPTIONAL peer dep — it's loaded lazily and only when you use push, so a
 *  project that doesn't want notifications never has to install it.
 *
 *  ── The three subsystems ────────────────────────────────────────────────────
 *
 *    sdk.chat      Real-time messaging over `conversation:<id>` — send / edit /
 *                  delete / react / typing / history / delta-sync, plus
 *                  delivered+read cursors that drive WhatsApp-style ticks.
 *    sdk.presence  Live presence over `user:<id>` — watch peers, set your own
 *                  status (online/away/busy/dnd) + custom message, idle→away.
 *    sdk.push      Firebase Cloud Messaging — register this browser's device
 *                  token with the SDK server so it gets closed-app alerts.
 *
 *  ── One config, one sequenced init ──────────────────────────────────────────
 *
 *    import { InstavcSdk } from './instavcMessenger'
 *
 *    const sdk = new InstavcSdk({
 *      userId:      myUserId,                        // your SecureID user id
 *      userName:    myName,
 *      getAppToken: async () => myApp.getAccessToken(),  // your app Bearer — the
 *                                                    // SDK exchanges it internally
 *      // socketUrl / sdkApiUrl are baked in — omit unless self-hosting.
 *      // OPTIONAL — omit to disable push entirely:
 *      firebase:   { apiKey, authDomain, projectId, storageBucket,
 *                    messagingSenderId, appId },
 *      vapidKey:   'BC...',                          // Web Push public key
 *    })
 *
 *    await sdk.init()                 // connect → presence(self) → push, in order
 *    await sdk.chat.join(convId)
 *    sdk.chat.on('message', (m) => render(m))
 *    sdk.presence.watch([peerId])
 *    sdk.presence.on('change', (id, info) => paint(id, info))
 *
 *  ── Sharing an existing socket (advanced) ───────────────────────────────────
 *
 *  If your app already owns a Phoenix `Socket` (e.g. the meeting/signaling
 *  connection), pass it as `socket` instead of `socketUrl` and the SDK will
 *  multiplex its channels onto yours — no second WebSocket (`getAppToken` is
 *  still used for REST/push):
 *
 *    const sdk = new InstavcSdk({ socket: myExistingSocket, sdkApiUrl, userId })
 *    await sdk.init()   // skips socket connect; goes straight to presence+push
 *
 *  ── Binding to any framework ────────────────────────────────────────────────
 *
 *    // React
 *    const msgs = useSyncExternalStore(
 *      (cb) => sdk.chat.on('message', () => cb()),
 *      () => sdk.chat.getMessages(convId),
 *    )
 * ============================================================================
 */

import { Socket, Channel, Presence } from 'phoenix'

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 0 — Endpoints
 *
 *  The Instavc stack is centralized shared infrastructure — every integrator
 *  talks to the SAME signaling socket + SDK API. So the endpoints are BAKED IN
 *  and you don't pass them. Override only for a private/self-hosted deployment
 *  (`socketUrl` / `sdkApiUrl` in the config still win when set).
 * ════════════════════════════════════════════════════════════════════════════ */

/** Default signaling (Phoenix) socket — used unless `config.socketUrl` overrides. */
export const DEFAULT_SOCKET_URL = 'wss://signal.instavc.ai/socket'
/** Default SDK API server (REST + push) — used unless `config.sdkApiUrl` overrides. */
export const DEFAULT_SDK_API_URL = 'https://sdkapi.instavc.ai'

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 1 — Shared types
 * ════════════════════════════════════════════════════════════════════════════ */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'
export type Logger = (level: LogLevel, msg: string, data?: unknown) => void

// ─── Chat types ───────────────────────────────────────────────────────────────

export interface ReactionAggregate {
  emoji: string
  count: number
  /** True if the local user reacted with this emoji. */
  byMe: boolean
}

/** Coarse render hint so you can pick a bubble without sniffing mime.
 *  `voice` is a recorded voice note; `audio` is an audio *file* — they share
 *  `audio/*` mime, so only an explicit kind can tell them apart. */
export type AttachmentKind = 'photo' | 'video' | 'audio' | 'voice' | 'document'

/**
 * An attachment on a message.
 *
 * **Bring-your-own-storage**: the SDK stores no bytes. Upload to whatever
 * storage you already run (your S3, a CDN, Cloudinary, …), then pass the
 * resulting URL plus metadata to `chat.send()`. The SDK persists the row and
 * broadcasts it live; recipients render straight from `url`.
 *
 * Reachability of `url` is your policy — a public CDN URL, a long-lived signed
 * URL, whatever your storage does. The SDK never fetches it.
 */
export interface ChatAttachment {
  /** Client-generated uuid. Auto-filled by `send()` when omitted; supplying it
   *  yourself makes a retried send idempotent instead of duplicating the row. */
  id?: string
  /** Absolute http(s) URL to the object. Other schemes are rejected server-side. */
  url: string
  kind: AttachmentKind
  mime: string
  sizeBytes: number
  filename?: string | null
  /** Poster/preview image — document previews, video posters. Drives the
   *  preview thumbnail in a document bubble. */
  thumbnailUrl?: string | null
  width?: number | null
  height?: number | null
  /** Audio/video duration — drives the voice-note waveform + running time. */
  durationMs?: number | null
}

export interface ChatMessage {
  id: string
  conversationId: string
  senderId: string | null
  type: string
  body: string | null
  replyTo?: string | null
  clientMsgId?: string | null
  editedAt?: string | null
  deletedAt?: string | null
  createdAt: string
  reactions?: ReactionAggregate[]
  attachments?: ChatAttachment[]
  /** History reads only, and only for YOUR OWN messages: this is a copy one of
   *  your broadcast lists delivered into the DM. The recipient's read of the
   *  same message never carries it. Live copies arrive without it — see
   *  `BroadcastSendResult.copies`. */
  viaBroadcast?: boolean
  /** Local-only: false until the server has acked/broadcast this message. */
  pending?: boolean
}

/** A participant's delivered/read high-water-marks (drives tick status). */
export interface ParticipantCursor {
  userId: string
  lastDeliveredAt: string | null
  lastReadAt: string | null
}

/** Tick status for one of *your own* sent messages. */
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read'

export interface SendInput {
  /** Optional when `attachments` is non-empty — an attachment with no caption
   *  is an ordinary message. A send with neither text nor attachments is
   *  rejected (`empty_message`). */
  body?: string
  /** Reuse to make send idempotent + to control the message id. Auto if omitted. */
  clientMsgId?: string
  messageType?: 'text' | 'system' | 'call'
  replyTo?: string | null
  /** Attachments you already uploaded to your own storage — URL + metadata.
   *  See {@link ChatAttachment}. */
  attachments?: ChatAttachment[]
}

export interface HistoryPage {
  items: ChatMessage[]
  nextCursor: string | null
  hasMore: boolean
  cursors?: ParticipantCursor[]
}

export interface SyncResult {
  items: ChatMessage[]
  cursors: ParticipantCursor[]
  syncedAt: string
}

export type ChatEvent =
  | 'message'          // (ChatMessage)      new/updated message (dedupe by id)
  | 'message:deleted'  // ({conversationId, messageId})
  | 'reaction'         // ({messageId, userId, emoji, removed})
  | 'read'             // ({conversationId, userId, lastReadAt})
  | 'delivered'        // ({conversationId, userId, lastDeliveredAt})
  | 'typing'           // ({conversationId, userId})
  | 'activity'         // ({conversationId, message})
  | 'conversation:removed' // ({conversationId}): you were removed from this
                       // conversation (or left it elsewhere); the server closes
                       // this channel right behind it with a clean phx_close,
                       // which phoenix-js does not auto-rejoin from
  | 'connected'
  | 'disconnected'
  | 'error'

// ─── Presence types ───────────────────────────────────────────────────────────

export type PresenceStatus = 'online' | 'away' | 'busy' | 'dnd' | 'offline'
/** Manual (user-pinned) statuses — the subset a user can explicitly choose. */
export type ManualStatus = 'online' | 'away' | 'busy' | 'dnd'

export interface PresenceInfo {
  userId:        string
  status:        PresenceStatus
  manualStatus:  ManualStatus | null
  customMessage: string | null
  inMeeting:     boolean
  lastSeenAt:    string | null
}

/** Raw per-socket meta as broadcast by Phoenix.Presence for `user:<id>`. */
interface RawMeta {
  status?:         string
  manual_status?:  string | null
  custom_message?: string | null
  in_meeting?:     boolean
  device?:         string
  user_name?:      string
}

export type PresenceEvent =
  | 'change'       // (userId, info)  — a watched user's presence changed
  | 'self'         // (info)          — my own presence changed
  | 'connected'    // ()              — socket opened
  | 'disconnected' // ()              — socket closed
  | 'error'        // (error)         — token/connect/write failure

type Handler = (...args: any[]) => void

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 2 — Config
 * ════════════════════════════════════════════════════════════════════════════ */

/** Firebase web config (public — safe in the bundle) needed for push. */
export interface FirebaseWebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId?: string
}

export interface InstavcSdkConfig {
  /** Phoenix socket endpoint. **Baked in** (`DEFAULT_SOCKET_URL`) — omit unless
   *  self-hosting. Ignored when `socket` is supplied. */
  socketUrl?: string
  /**
   * Supply an existing, app-managed Phoenix `Socket` to multiplex onto (no
   * second WebSocket). When set, `socketUrl` / `getAppToken` are not used for the
   * connection (but `getAppToken` is still used for REST/push) and `init()` will
   * not open or close the socket.
   */
  socket?: Socket
  /** Base URL of the SDK server (REST reads + push token store). **Baked in**
   *  (`DEFAULT_SDK_API_URL`) — omit unless self-hosting. */
  sdkApiUrl?: string
  /** The authenticated user's id (must match the Phoenix token's `sub`) — the
   *  SecureID user id. Used for the socket + meetings. */
  userId: string
  /**
   * Per-org chat/presence identity (`contactPresenceId`, from `contacts.lookupSelf`).
   * When set, chat ("is me" / `senderId`) and presence (`user:<id>` topic) use
   * THIS id instead of `userId`, so all of an org's apps share one identity.
   * Falls back to `userId` when unset (pre-consent / legacy).
   */
  presenceId?: string
  /** Company/brand name for THIS app — used as the default `contactOrgName` when
   *  `contacts.registerSelf()` is called without one. App-provided (mandatory on
   *  the server; defaults to 'instavc'). Set it once here instead of per call. */
  contactOrgName?: string
  /** Sub-organisation scope for THIS app — a UUID. When set, it's the
   *  default `subId` for `registerSelf()` / `lookupSelf()`, giving a
   *  per-sub-org identity. Also pass it to your token mint so the socket resolves
   *  the same contact. Omit for a single-sub-org app. */
  subId?: string
  /** Optional display name, forwarded as a socket param + presence meta. */
  userName?: string
  /**
   * Return your app's current Bearer — the SecureID / host-app token. The SDK
   * **exchanges it internally** at sdk-server's mint for an SDK session token
   * (cached + auto-refreshed) and uses THAT for BOTH the signaling socket AND
   * sdk-server REST/push. You never mint or cache tokens yourself. May be sync or
   * async — return a *fresh* app token each call so re-mints survive app-token
   * rotation. Required unless you inject a pre-connected `socket` (still needed
   * for REST).
   */
  getAppToken?: () => string | Promise<string>
  /** Stable per-install device id. Sent with the FCM token so push fan-out can
   *  skip this device while notifying the user's other, offline devices. When
   *  the SDK opens its own socket, it's also added to the socket params. */
  deviceId?: string

  // ── Push (optional) ──
  /** Firebase web config. Omit to disable push. */
  firebase?: FirebaseWebConfig
  /** Web Push VAPID **public** key. Required for push registration. */
  vapidKey?: string
  /** Service worker path for background messages. Default `/firebase-messaging-sw.js`. */
  swPath?: string

  // ── Tuning ──
  socketParams?: Record<string, unknown>
  reconnectAfterMs?: (tries: number) => number
  heartbeatIntervalMs?: number
  /** Auto-send a `delivered` cursor when someone else's message arrives. Default true. */
  autoDelivered?: boolean
  /** Idle→away threshold (ms). Default 5 min. Only used by startIdleDetection. */
  idleThresholdMs?: number
  logger?: Logger
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 3 — Pure helpers
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * Exchange the app's Bearer for an **SDK session token** at sdk-server's mint
 * (`POST /api/v1/auth/token`). This ONE token is accepted by BOTH the signaling
 * socket AND sdk-server REST.
 *
 * You normally do **not** call this directly — pass `getAppToken` to `InstavcSdk`
 * and it runs this exchange internally (cached + refreshed) via
 * `SessionTokenProvider`. Exported for advanced/standalone use.
 *
 * "Exchange" mode: `bearer` is the app's current token (SecureID RS256 / host-app
 * token / an existing SDK token) — sdk-server verifies it and re-mints.
 */
export async function fetchSdkSessionToken(opts: {
  /** SDK API base. **Baked in** (`DEFAULT_SDK_API_URL`) — omit unless self-hosting. */
  sdkApiUrl?: string
  bearer: string | (() => string | Promise<string>)
  /** Scope the minted token to a sub-org (bakes it into the token so socket +
   *  REST resolve the same per-sub-org contact). Omit for single-sub-org apps. */
  subId?: string
  fetchImpl?: typeof fetch
}): Promise<{ accessToken: string; expiresIn: number }> {
  const bearer = typeof opts.bearer === 'function' ? await opts.bearer() : opts.bearer
  const f = opts.fetchImpl ?? fetch
  const base = (opts.sdkApiUrl ?? DEFAULT_SDK_API_URL).replace(/\/+$/, '')
  const res = await f(`${base}/api/v1/auth/token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(opts.subId ? { subId: opts.subId } : {}),
  })
  if (!res.ok) throw new Error(`fetchSdkSessionToken failed: ${res.status}`)
  return res.json() as Promise<{ accessToken: string; expiresIn: number }>
}

/**
 * The `sub` claim of a JWT, read WITHOUT verifying.
 *
 * Used only to notice that a DIFFERENT USER is behind the app token. Verifying
 * is the server's job; nothing here trusts the value for authorization.
 */
function jwtSubject(token: string): string {
  try {
    const payload = token.split('.')[1]
    if (!payload) return token
    // atob yields BYTES, not text. Decoding them as UTF-8 rather than reading
    // them as latin1 keeps any non-ASCII claim in the payload from corrupting
    // the parse. (`sub` itself is ASCII in practice, so this is belt-and-braces.)
    const bin = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
    return (JSON.parse(json) as { sub?: string }).sub || token
  } catch {
    // Not a JWT (an opaque host-app token is legal here) — fall back to the
    // whole string, which is the old behaviour and still correct, just chattier.
    return token
  }
}

/**
 * Internal token manager. Turns the app's `getAppToken` bearer into an SDK
 * session token via the exchange mint, then CACHES it and refreshes ~60s before
 * expiry. One instance is shared across the socket + every REST module, so the
 * mint runs once per token lifetime — not once per call.
 *
 * The cache is keyed on the app token's `sub`, NOT the token string. Host apps
 * with short rotating access tokens (ours is 15 minutes) hand us a different
 * string every rotation, and comparing strings would discard a still-valid SDK
 * token — which lasts an hour — several times an hour, putting a mint
 * round-trip in the socket's path each time. What actually invalidates an SDK
 * token is a different user, and that is what `sub` captures.
 */
class SessionTokenProvider {
  private cfg: InstavcSdkConfig
  private cache: { token: string; exp: number; from: string } | null = null
  private inflight: Promise<string> | null = null
  constructor(cfg: InstavcSdkConfig) { this.cfg = cfg }

  async get(): Promise<string> {
    if (!this.cfg.getAppToken) {
      throw new Error('InstavcSdk: `getAppToken` is required (or inject a pre-connected `socket`)')
    }
    const bearer = await this.cfg.getAppToken()
    if (!bearer) throw new Error('InstavcSdk: getAppToken returned an empty token')

    const subject = jwtSubject(bearer)
    const now = Date.now()
    if (this.cache && this.cache.from === subject && now < this.cache.exp) return this.cache.token
    if (this.inflight) return this.inflight

    this.inflight = (async () => {
      try {
        const { accessToken, expiresIn } = await fetchSdkSessionToken({
          sdkApiUrl: this.cfg.sdkApiUrl,
          bearer,
          subId: this.cfg.subId,
        })
        this.cache = {
          token: accessToken,
          exp: Date.now() + Math.max(expiresIn - 60, 30) * 1000,
          from: subject,
        }
        return accessToken
      } finally {
        this.inflight = null
      }
    })()
    return this.inflight
  }

  /** The most recent token, WITHOUT a round trip — null before the first
   *  successful mint, and possibly expired. For the one caller that cannot
   *  await: phoenix.js evaluates its `params` function synchronously on every
   *  connection attempt. Pair it with [refresh] so a retry after expiry has a
   *  live value to read here. */
  peek(): string | null {
    return this.cache?.token ?? null
  }

  /** Re-mint if the cache has expired, ignoring failures. Fire-and-forget from
   *  socket error/close handlers so the NEXT reconnect attempt's `params` picks
   *  up a fresh token instead of replaying the dead one forever. */
  refresh(): void {
    void this.get().catch(() => {})
  }

  clear(): void {
    this.cache = null
    this.inflight = null
  }
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'msg-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

// ── Presence aggregation (server-parity: manual > in-meeting > online > offline)

const STATUS_RANK: Record<string, number> = { dnd: 4, busy: 3, away: 2, online: 1 }
const MANUAL_STATUSES: ManualStatus[] = ['online', 'away', 'busy', 'dnd']

function pickManualStatus(metas: RawMeta[]): ManualStatus | null {
  const values = metas
    .map((m) => m.manual_status)
    .filter((v): v is ManualStatus =>
      v === 'dnd' || v === 'busy' || v === 'away' || v === 'online')
  if (values.length === 0) return null
  return values.sort((a, b) => (STATUS_RANK[b] ?? 0) - (STATUS_RANK[a] ?? 0))[0]
}

/** Collapse a user's per-socket metas into one public snapshot. */
export function aggregate(userId: string, metas: RawMeta[]): PresenceInfo {
  if (metas.length === 0) {
    return {
      userId, status: 'offline',
      manualStatus: null, customMessage: null, inMeeting: false, lastSeenAt: null,
    }
  }
  const manualStatus = pickManualStatus(metas)
  const inMeeting = metas.some((m) => m.in_meeting === true)
  const customMessage =
    metas
      .map((m) => m.custom_message)
      .find((m): m is string => typeof m === 'string' && m !== '') ?? null
  const status: PresenceStatus = manualStatus ?? (inMeeting ? 'busy' : 'online')
  return { userId, status, manualStatus, customMessage, inMeeting, lastSeenAt: null }
}

// ── Relative-time helper (localized "last seen N ago") ──

const _rtfCache = new Map<string, Intl.RelativeTimeFormat>()
function rtfFor(locale: string): Intl.RelativeTimeFormat {
  let f = _rtfCache.get(locale)
  if (!f) { f = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }); _rtfCache.set(locale, f) }
  return f
}

/** Format an ISO8601 timestamp as a short "X ago" string. Empty for null/bad. */
export function formatLastSeen(iso: string | null, now: number = Date.now(), locale = 'en'): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const diffSec = Math.round((t - now) / 1000)
  const abs = Math.abs(diffSec)
  const rtf = rtfFor(locale)
  if (abs < 60)          return rtf.format(Math.round(diffSec), 'second')
  if (abs < 3600)        return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400)       return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 86400 * 7)   return rtf.format(Math.round(diffSec / 86400), 'day')
  if (abs < 86400 * 30)  return rtf.format(Math.round(diffSec / 86400 / 7), 'week')
  if (abs < 86400 * 365) return rtf.format(Math.round(diffSec / 86400 / 30), 'month')
  return rtf.format(Math.round(diffSec / 86400 / 365), 'year')
}

/**
 * Multi-tab leader election via the Web Locks API. Invokes `onChange(true)`
 * for exactly one tab at a time (so presence toasts fire once, not per-tab).
 * Degrades to "everyone is leader" where Web Locks is unavailable.
 */
export function electTabLeader(
  onChange: (isLeader: boolean) => void,
  lockName = 'presence-leader',
): () => void {
  const nav = typeof navigator !== 'undefined' ? (navigator as any) : undefined
  if (!nav?.locks) { onChange(true); return () => {} }
  let release: (() => void) | null = null
  let cancelled = false
  void nav.locks.request(lockName, { mode: 'exclusive' }, () => {
    if (cancelled) return Promise.resolve()
    onChange(true)
    return new Promise<void>((resolve) => { release = resolve })
  })
  return () => { cancelled = true; onChange(false); release?.() }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 4 — Socket manager (owned OR injected)
 * ════════════════════════════════════════════════════════════════════════════ */

class SocketManager {
  socket: Socket | null = null
  private owned = false
  private state: 'idle' | 'connecting' | 'open' | 'closed' = 'idle'
  private listeners = { open: new Set<Handler>(), close: new Set<Handler>(), error: new Set<Handler>() }
  private cfg: InstavcSdkConfig
  private tokens: SessionTokenProvider

  constructor(cfg: InstavcSdkConfig, tokens?: SessionTokenProvider) {
    this.cfg = cfg
    this.tokens = tokens ?? new SessionTokenProvider(cfg)
    if (cfg.socket) { this.socket = cfg.socket; this.owned = false; this.state = 'open' }
  }

  onOpen(h: Handler) { this.listeners.open.add(h) }
  onClose(h: Handler) { this.listeners.close.add(h) }
  onError(h: Handler) { this.listeners.error.add(h) }

  isReady(): boolean { return !!this.socket && this.state !== 'closed' }

  async connect(): Promise<Socket> {
    // Injected socket — nothing to open; the host app owns its lifecycle.
    if (this.cfg.socket) { this.socket = this.cfg.socket; return this.socket }
    if (this.socket && this.state !== 'closed') return this.socket

    // The SDK session token (exchanged from the app token + cached internally).
    let token: string
    try { token = await this.tokens.get() }
    catch (err) { this.listeners.error.forEach((h) => h(err)); throw err }

    // Baked default unless the app overrides for a self-hosted deployment.
    const socketUrl = this.cfg.socketUrl ?? DEFAULT_SOCKET_URL
    const socket = new Socket(socketUrl, {
      // A FUNCTION, not a literal: phoenix.js re-evaluates it on every
      // connection attempt, including its own auto-reconnects. A captured
      // token object would be replayed verbatim on each retry, so once the
      // ~1h session token expired the server would refuse every attempt
      // forever while the app kept believing it was merely reconnecting.
      params: () => ({
        token: this.tokens.peek() ?? token,
        ...(this.cfg.deviceId ? { device_id: this.cfg.deviceId } : {}),
        ...(this.cfg.socketParams ?? {}),
      }),
      reconnectAfterMs: this.cfg.reconnectAfterMs ?? ((tries) => Math.min(1000 * Math.pow(2, tries), 60_000)),
      heartbeatIntervalMs: this.cfg.heartbeatIntervalMs ?? 30_000,
      logger: (kind: string, msg: string, data: unknown) =>
        this.cfg.logger?.('debug', `[phoenix] ${kind} ${msg}`, data),
    })
    this.socket = socket
    this.owned = true
    this.state = 'connecting'
    socket.onOpen(() => { this.state = 'open'; this.listeners.open.forEach((h) => h()) })
    // Re-mint on the way down, so the token `params` reads on the next retry is
    // a live one. Without this the provider only refreshes when some REST call
    // happens to ask, which a backgrounded tab may never do.
    socket.onClose(() => {
      this.state = 'closed'
      this.tokens.refresh()
      this.listeners.close.forEach((h) => h())
    })
    socket.onError((err: unknown) => {
      this.tokens.refresh()
      this.listeners.error.forEach((h) => h(err))
    })
    socket.connect()
    return socket
  }

  /** Only tears down a socket the SDK opened itself; never an injected one. */
  disconnect(): void {
    if (this.owned && this.socket) { try { this.socket.disconnect() } catch {} }
    this.socket = null
    this.state = 'closed'
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 5 — Chat module (`conversation:<id>`)
 * ════════════════════════════════════════════════════════════════════════════ */

interface ConvHandle { channel: Channel; joined: boolean }

export class ChatModule {
  private convs = new Map<string, ConvHandle>()
  private listeners = new Map<ChatEvent, Set<Handler>>()
  /** Per-conversation message cache, keyed by message id. */
  private store = new Map<string, Map<string, ChatMessage>>()
  /** Per-conversation participant cursors, keyed by userId (drives ticks). */
  private cursors = new Map<string, Map<string, ParticipantCursor>>()
  /** Per-conversation delivered high-water-mark. */
  private deliveredHW = new Map<string, string>()

  private sm: SocketManager
  private userId: string
  private autoDelivered: boolean
  private logger?: Logger
  /** The `user:<id>` topic, when bound — see `bindOwnerChannel`. */
  private ownerChannel: Channel | null = null

  constructor(sm: SocketManager, userId: string, autoDelivered: boolean, logger?: Logger) {
    this.sm = sm
    this.userId = userId
    this.autoDelivered = autoDelivered
    this.logger = logger
  }

  private get socket(): Socket {
    if (!this.sm.socket) throw new Error('chat: socket not connected — call sdk.init() first')
    return this.sm.socket
  }

  // ── Conversations ──
  async join(conversationId: string): Promise<void> {
    const existing = this.convs.get(conversationId)
    if (existing?.joined) return

    const channel = this.socket.channel(`conversation:${conversationId}`, {})

    channel.on('message:new', (p: { message: ChatMessage }) => {
      this.ingest(conversationId, p.message)
      if (this.autoDelivered && p.message.senderId && p.message.senderId !== this.userId) {
        this.markDelivered(conversationId, p.message.createdAt)
      }
    })
    channel.on('message:updated', (p: { message: ChatMessage }) => this.ingest(conversationId, p.message))
    channel.on('message:deleted', (p: { conversationId: string; messageId: string }) => {
      const conv = this.store.get(p.conversationId)
      const m = conv?.get(p.messageId)
      if (m) { m.deletedAt = new Date().toISOString(); m.body = null; this.emit('message', m) }
      this.emit('message:deleted', p)
    })
    channel.on('reaction:added', (p: { messageId: string; userId: string; emoji: string }) => {
      this.applyReaction(conversationId, p.messageId, p.emoji, +1, p.userId === this.userId)
      this.emit('reaction', { ...p, removed: false })
    })
    channel.on('reaction:removed', (p: { messageId: string; userId: string; emoji: string }) => {
      this.applyReaction(conversationId, p.messageId, p.emoji, -1, p.userId === this.userId)
      this.emit('reaction', { ...p, removed: true })
    })
    channel.on('read:updated', (p: { conversationId: string; userId: string; lastReadAt: string }) => {
      this.mergeCursor(conversationId, p.userId, { lastReadAt: p.lastReadAt })
      this.emit('read', p)
    })
    channel.on('delivered:updated', (p: { conversationId: string; userId: string; lastDeliveredAt: string }) => {
      this.mergeCursor(conversationId, p.userId, { lastDeliveredAt: p.lastDeliveredAt })
      this.emit('delivered', p)
    })
    channel.on('typing', (p: unknown) => this.emit('typing', p))
    // NOTE: `conversation:activity` is deliberately NOT bound here. The server
    // broadcasts it to the recipient's OWNER topic (`user:<id>`), precisely so
    // it reaches someone who is NOT on this conversation topic — see
    // `bindOwnerChannel` below. Binding it here could only ever fire for a
    // conversation you already have open, which is the one case that does not
    // need a nudge, and in practice it never fired at all.
    // Fired once, right before the server closes this channel with a clean
    // phx_close (removal, or the user left on another device). phoenix-js
    // only auto-rejoins off phx_error, never off phx_close, so nothing else
    // is needed here to stop a retry loop.
    channel.on('conversation:removed', (p: { conversationId?: string }) => {
      // Drop our handle: the server closes this channel immediately behind this
      // frame, so the entry left in `convs` describes a dead channel.
      //
      // Leaving it cost two things. `isJoined()` reported true for a
      // conversation we had been removed from — an app gating its composer on
      // it offers a live input for a group it cannot post to. Worse, `join()`
      // early-returns while `joined` is true, so being re-added to the group
      // left the SDK unable to rejoin for the rest of the session: `join()`
      // resolved without ever reaching the server, and no message arrived
      // again until a reload.
      this.convs.delete(p?.conversationId ?? conversationId)
      this.emit('conversation:removed', { conversationId: p?.conversationId ?? conversationId })
    })

    const handle: ConvHandle = { channel, joined: false }
    this.convs.set(conversationId, handle)

    await new Promise<void>((resolve, reject) => {
      channel.join()
        .receive('ok', () => { handle.joined = true; resolve() })
        .receive('error', (err: unknown) => {
          try { channel.leave() } catch {}
          this.convs.delete(conversationId)
          reject(new Error(`join conversation:${conversationId} failed: ${JSON.stringify(err)}`))
        })
        .receive('timeout', () => {
          try { channel.leave() } catch {}
          this.convs.delete(conversationId)
          reject(new Error(`join conversation:${conversationId} timed out`))
        })
    })
  }

  leave(conversationId: string): void {
    const h = this.convs.get(conversationId)
    if (h) { try { h.channel.leave() } catch {}; this.convs.delete(conversationId) }
  }

  isJoined(conversationId: string): boolean {
    return this.convs.get(conversationId)?.joined === true
  }

  // ── Send / edit / delete / react ──
  async send(conversationId: string, input: SendInput): Promise<ChatMessage> {
    const h = this.convs.get(conversationId)
    if (!h?.joined) throw new Error(`not joined to conversation:${conversationId}`)

    const body = input.body ?? ''
    // Stamp ids client-side so the optimistic bubble, the live broadcast and
    // the stored row all share one id — and so a retried send is idempotent
    // rather than inserting the attachment twice.
    const attachments = (input.attachments ?? []).map((a) => ({ ...a, id: a.id || uuid() }))
    if (body.trim() === '' && attachments.length === 0) {
      throw new Error('send needs a body or at least one attachment')
    }

    const id = input.clientMsgId || uuid()
    const optimistic: ChatMessage = {
      id,
      conversationId,
      senderId: this.userId,
      type: input.messageType || 'text',
      body: body || null,
      replyTo: input.replyTo ?? null,
      clientMsgId: id,
      attachments,
      createdAt: new Date().toISOString(),
      pending: true,
    }
    this.ingest(conversationId, optimistic)

    return new Promise<ChatMessage>((resolve, reject) => {
      h.channel.push('message:send', {
        body,
        clientMsgId: id,
        messageType: input.messageType || 'text',
        replyTo: input.replyTo ?? null,
        attachments,
      })
        .receive('ok', (message: ChatMessage) => {
          this.ingest(conversationId, { ...message, pending: false })
          resolve({ ...message, pending: false })
        })
        .receive('error', (err: unknown) => {
          this.store.get(conversationId)?.delete(id)
          this.emit('message:deleted', { conversationId, messageId: id })
          reject(new Error(`send failed: ${JSON.stringify(err)}`))
        })
        .receive('timeout', () => reject(new Error('send timed out')))
    })
  }

  /** Edit a message's body. Server broadcasts `message:updated`. */
  async edit(conversationId: string, messageId: string, body: string): Promise<void> {
    const prev = this.store.get(conversationId)?.get(messageId)
    if (prev) this.ingest(conversationId, { ...prev, body, editedAt: new Date().toISOString() })
    await this.request(conversationId, 'message:edit', { messageId, body })
  }

  /** Soft-delete a message. Server broadcasts `message:deleted`. */
  async delete(conversationId: string, messageId: string): Promise<void> {
    const prev = this.store.get(conversationId)?.get(messageId)
    if (prev) this.ingest(conversationId, { ...prev, deletedAt: new Date().toISOString(), body: null })
    await this.request(conversationId, 'message:delete', { messageId })
  }

  /** Add or remove a reaction. Optimistic; server echoes `reaction:added/removed`. */
  async react(conversationId: string, messageId: string, emoji: string, action: 'add' | 'remove'): Promise<void> {
    this.applyReaction(conversationId, messageId, emoji, action === 'add' ? +1 : -1, true)
    await this.request(conversationId, action === 'add' ? 'reaction:add' : 'reaction:remove', { messageId, emoji })
  }

  /** Fire-and-forget typing indicator (server rate-limits to 1/3s). */
  sendTyping(conversationId: string): void {
    const h = this.convs.get(conversationId)
    if (h?.joined) { try { h.channel.push('typing', {}) } catch {} }
  }

  // ── History / delta sync ──
  async fetchHistory(conversationId: string, opts: { before?: string; limit?: number } = {}): Promise<HistoryPage> {
    const h = this.convs.get(conversationId)
    if (!h?.joined) throw new Error(`not joined to conversation:${conversationId}`)
    return new Promise((resolve, reject) => {
      h.channel.push('history:fetch', { before: opts.before ?? null, limit: opts.limit ?? 50 })
        .receive('ok', (res: HistoryPage) => {
          for (const m of res.items) this.ingest(conversationId, { ...m, pending: false }, false)
          resolve(res)
        })
        .receive('error', (err: unknown) => reject(new Error(`history:fetch failed: ${JSON.stringify(err)}`)))
        .receive('timeout', () => reject(new Error('history:fetch timed out')))
    })
  }

  async sync(conversationId: string, since: string | null = null): Promise<SyncResult> {
    const h = this.convs.get(conversationId)
    if (!h?.joined) throw new Error(`not joined to conversation:${conversationId}`)
    return new Promise((resolve, reject) => {
      h.channel.push('sync:fetch', { since })
        .receive('ok', (res: { items: ChatMessage[]; cursors?: ParticipantCursor[]; syncedAt: string }) => {
          for (const m of res.items) this.ingest(conversationId, { ...m, pending: false }, false)
          for (const c of res.cursors ?? []) {
            this.mergeCursor(conversationId, c.userId, { lastDeliveredAt: c.lastDeliveredAt, lastReadAt: c.lastReadAt })
          }
          resolve({ items: res.items, cursors: res.cursors ?? [], syncedAt: res.syncedAt })
        })
        .receive('error', (err: unknown) => reject(new Error(`sync:fetch failed: ${JSON.stringify(err)}`)))
        .receive('timeout', () => reject(new Error('sync:fetch timed out')))
    })
  }

  // ── Delivered / read cursors (ticks) ──
  markRead(conversationId: string, atIso: string): void {
    const h = this.convs.get(conversationId)
    if (!h?.joined || !atIso) return
    this.mergeCursor(conversationId, this.userId, { lastReadAt: atIso })
    try { h.channel.push('read:update', { at: atIso }) } catch {}
  }

  markDelivered(conversationId: string, atIso: string): void {
    const h = this.convs.get(conversationId)
    if (!h?.joined || !atIso) return
    const prev = this.deliveredHW.get(conversationId)
    if (prev && prev >= atIso) return
    this.deliveredHW.set(conversationId, atIso)
    this.mergeCursor(conversationId, this.userId, { lastDeliveredAt: atIso })
    try { h.channel.push('delivered:update', { at: atIso }) } catch {}
  }

  // ── Snapshots / status ──
  getCursors(conversationId: string): ParticipantCursor[] {
    return Array.from(this.cursors.get(conversationId)?.values() ?? [])
  }

  statusFor(conversationId: string, message: ChatMessage): MessageStatus {
    if (message.pending) return 'sending'
    const others = this.getCursors(conversationId).filter((c) => c.userId !== message.senderId)
    if (others.length === 0) return 'sent'
    let read: string | null = null
    let delivered: string | null = null
    let readAll = true
    let deliveredAll = true
    for (const c of others) {
      if (!c.lastReadAt) readAll = false
      else if (read === null || c.lastReadAt < read) read = c.lastReadAt
      if (!c.lastDeliveredAt) deliveredAll = false
      else if (delivered === null || c.lastDeliveredAt < delivered) delivered = c.lastDeliveredAt
    }
    if (readAll && read && message.createdAt <= read) return 'read'
    if (deliveredAll && delivered && message.createdAt <= delivered) return 'delivered'
    return 'sent'
  }

  getMessages(conversationId: string): ChatMessage[] {
    const conv = this.store.get(conversationId)
    if (!conv) return []
    // `?? ''` is belt-and-braces behind the `ingest` guard above: ordering a
    // thread is the one read every renderer makes, so it must not be the thing
    // that throws if a malformed row ever reaches the store again.
    return Array.from(conv.values())
      .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))
  }

  /** Seed history (e.g. from a persistent cache or REST) into the local cache. */
  seed(conversationId: string, messages: ChatMessage[]): void {
    for (const m of messages) this.ingest(conversationId, m, false)
  }

  // ── Events ──
  on(event: ChatEvent, handler: Handler): () => void {
    let set = this.listeners.get(event)
    if (!set) { set = new Set(); this.listeners.set(event, set) }
    set.add(handler)
    return () => set!.delete(handler)
  }

  private emit(event: ChatEvent, ...args: any[]): void {
    this.listeners.get(event)?.forEach((h) => {
      try { h(...args) } catch (e) { this.logger?.('error', 'chat listener threw', e) }
    })
  }

  private ingest(conversationId: string, message: ChatMessage, emit = true): void {
    let conv = this.store.get(conversationId)
    if (!conv) { conv = new Map(); this.store.set(conversationId, conv) }
    const prev = conv.get(message.id)

    // `message:updated` carries a PARTIAL message — id, body, editedAt and
    // nothing else. Merging that over a `prev` we have is the whole point; but
    // with no `prev` it would CREATE an entry with no `createdAt`, and every
    // read that orders a thread (`getMessages`) then sorts on undefined.
    //
    // A partial update for an id we don't hold is not ours to apply: the thread
    // is paginated, so an edit to a message older than the loaded page is
    // ordinary — the peer simply hasn't got that row, and will pick the edit up
    // with the row itself on the next `fetchHistory`/`sync`. Dropping it is what
    // the app-side store already does (`replaceWireMessage` returns early when
    // the id isn't in the list).
    if (!prev && !message.createdAt) return

    const merged: ChatMessage = { ...prev, ...message, pending: message.pending ?? false }
    conv.set(message.id, merged)
    if (emit) this.emit('message', merged)
  }

  private request(conversationId: string, event: string, payload: Record<string, unknown>): Promise<unknown> {
    const h = this.convs.get(conversationId)
    if (!h?.joined) return Promise.reject(new Error(`not joined to conversation:${conversationId}`))
    return new Promise((resolve, reject) => {
      h.channel.push(event, payload)
        .receive('ok', (res: unknown) => resolve(res))
        .receive('error', (err: unknown) => reject(new Error(`${event} failed: ${JSON.stringify(err)}`)))
        .receive('timeout', () => reject(new Error(`${event} timed out`)))
    })
  }

  private mergeCursor(conversationId: string, userId: string, patch: Partial<ParticipantCursor>): void {
    let conv = this.cursors.get(conversationId)
    if (!conv) { conv = new Map(); this.cursors.set(conversationId, conv) }
    const prev = conv.get(userId) ?? { userId, lastDeliveredAt: null, lastReadAt: null }
    const next: ParticipantCursor = { ...prev }
    if (patch.lastDeliveredAt && (!prev.lastDeliveredAt || patch.lastDeliveredAt > prev.lastDeliveredAt)) {
      next.lastDeliveredAt = patch.lastDeliveredAt
    }
    if (patch.lastReadAt && (!prev.lastReadAt || patch.lastReadAt > prev.lastReadAt)) {
      next.lastReadAt = patch.lastReadAt
    }
    conv.set(userId, next)
  }

  private applyReaction(conversationId: string, messageId: string, emoji: string, delta: number, byMe: boolean): void {
    const conv = this.store.get(conversationId)
    const msg = conv?.get(messageId)
    if (!conv || !msg) return
    const reactions = (msg.reactions ?? []).map((r) => ({ ...r }))
    const i = reactions.findIndex((r) => r.emoji === emoji)
    if (i === -1) {
      if (delta > 0) reactions.push({ emoji, count: 1, byMe })
    } else if (byMe && delta > 0 && reactions[i].byMe) {
      // Our own add, already applied optimistically in `react()`, now echoed
      // back by the server — the broadcast goes to the whole topic including
      // the reactor. Counting it twice is what made one tap render 2 (and a
      // re-add render 4) until the next fetchHistory recomputed it.
      return
    } else if (byMe && delta < 0 && !reactions[i].byMe) {
      // Mirror of the above for a remove: our optimistic remove already
      // cleared `byMe`, so the echoed removal must not decrement again.
      return
    } else {
      reactions[i].count = Math.max(0, reactions[i].count + delta)
      if (byMe) reactions[i].byMe = delta > 0
      if (reactions[i].count === 0) reactions.splice(i, 1)
    }
    const merged: ChatMessage = { ...msg, reactions }
    conv.set(messageId, merged)
    this.emit('message', merged)
  }

  /**
   * Bind the events that arrive on the user's OWN `user:<id>` topic rather than
   * on a conversation topic. Wired by `InstavcSdk.init` through
   * `PresenceModule.onOwnerChannel`, the same hook the call ring uses — the
   * owner topic is already open, so this costs no extra subscription.
   *
   * `conversation:activity` is the nudge for a message in a thread the user has
   * NOT joined (`notify_participants` on the server). It is the only way an app
   * learns about an incoming DM before opening it, so it cannot live on the
   * conversation channel. Re-binding on a reconnect is safe: phoenix `Channel`
   * identity changes, and `bind` is per-channel.
   */
  bindOwnerChannel(channel: Channel): void {
    if (this.ownerChannel === channel) return
    this.ownerChannel = channel
    channel.on('conversation:activity', (p: unknown) => this.emit('activity', p))
  }

  /** Leave every conversation channel (called on SDK disconnect). */
  teardown(): void {
    for (const { channel } of this.convs.values()) { try { channel.leave() } catch {} }
    this.convs.clear()
    this.ownerChannel = null
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 6 — Presence module (`user:<id>`)
 * ════════════════════════════════════════════════════════════════════════════ */

interface ChannelHandle { channel: Channel; presence: Presence; isOwner: boolean }

export class PresenceModule {
  private channels = new Map<string, ChannelHandle>()
  private denied = new Set<string>()
  private store = new Map<string, PresenceInfo>()
  private listeners = new Map<PresenceEvent, Set<Handler>>()

  private idleTimer: ReturnType<typeof setInterval> | null = null
  private idleTeardown: (() => void) | null = null
  private lastActivity = Date.now()
  private autoSetAway = false

  private sm: SocketManager
  private userId: string
  private idleThresholdMs: number
  private logger?: Logger

  // Hooks that let a sibling module (e.g. CallsModule) ride the SAME owner
  // `user:<id>` channel instead of joining the topic a second time (which would
  // double-track this user's presence).
  private ownerBinders = new Set<(ch: Channel) => void>()

  constructor(sm: SocketManager, userId: string, idleThresholdMs: number, logger?: Logger) {
    this.sm = sm
    this.userId = userId
    this.idleThresholdMs = idleThresholdMs
    this.logger = logger
  }

  private get socket(): Socket | null { return this.sm.socket }

  /** Join the owner topic so self-status is tracked + writable. */
  start(): void { this.watch([this.userId]) }

  // ── Watch ──
  watch(userIds: string[]): void { for (const id of userIds) this.joinTopic(id) }

  setWatch(userIds: string[]): void {
    const target = new Set(userIds.filter(Boolean))
    target.add(this.userId)
    for (const [id, h] of this.channels) {
      if (!target.has(id)) { try { h.channel.leave() } catch {}; this.channels.delete(id) }
    }
    for (const id of target) this.joinTopic(id)
  }

  unwatch(userIds: string[]): void {
    for (const id of userIds) {
      if (id === this.userId) continue
      const h = this.channels.get(id)
      if (h) { try { h.channel.leave() } catch {}; this.channels.delete(id) }
    }
  }

  private joinTopic(userId: string): void {
    if (!userId || this.channels.has(userId) || this.denied.has(userId)) return
    const socket = this.socket
    if (!socket) return
    const isOwner = userId === this.userId
    const channel = socket.channel(`user:${userId}`, {})
    const presence = new Presence(channel)

    presence.onSync(() => {
      const rows = presence.list<{ key: string; metas: RawMeta[] }>(
        (key: string, p: { metas: RawMeta[] }) => ({ key, metas: p.metas }),
      )
      const found = rows.find((r) => r.key === userId)
      this.applyInfo(aggregate(userId, found?.metas ?? []))
    })

    channel.on('presence:offline_confirmed', (payload: { user_id: string; last_seen_at: string }) => {
      const prev = this.store.get(payload.user_id) ?? aggregate(payload.user_id, [])
      this.applyInfo({ ...prev, status: 'offline', lastSeenAt: payload.last_seen_at })
    })

    // The server has withdrawn permission to watch this user (a block, either
    // direction) and is closing the topic right behind this frame. Resolve them
    // to offline: the store holds the last status we were sent, and with the
    // subscription gone nothing would ever arrive to correct it — so without
    // this they would sit at "online" forever. Same shape as the blocked-user
    // masking in the app-side presence store.
    channel.on('presence:watch_revoked', (payload: { user_id: string }) => {
      const id = payload?.user_id ?? userId
      this.channels.delete(id)
      this.applyInfo(aggregate(id, []))
      // NOT added to `denied`: that set is never cleared, so marking them here
      // would make this user unwatchable for the rest of the session — and the
      // block that caused this can be lifted. A later `watch()` re-joins and
      // lets the SERVER re-run the ACL: still blocked ⇒ a normal join denial
      // (which does set `denied`, bounded to one attempt); unblocked ⇒ it
      // simply works again.
    })

    // Let sibling modules bind their own handlers to the owner channel (call
    // ring events, etc.) BEFORE we join, so nothing pushed on connect is missed.
    if (isOwner) this.ownerBinders.forEach((fn) => this.safeBind(fn, channel))

    channel.join()
      .receive('ok', () => this.channels.set(userId, { channel, presence, isOwner }))
      .receive('error', (err: unknown) => {
        try { channel.leave() } catch {}
        this.channels.delete(userId)
        this.denied.add(userId)
        this.applyInfo(aggregate(userId, []))
        this.logger?.('warn', `presence join denied for ${userId}`, err)
      })

    this.channels.set(userId, { channel, presence, isOwner })
  }

  // ── Self status ──
  async setStatus(status: ManualStatus | null): Promise<void> {
    if (status !== null && !MANUAL_STATUSES.includes(status)) return
    const prev = this.store.get(this.userId)
    this.applyInfo({
      ...(prev ?? aggregate(this.userId, [])),
      manualStatus: status,
      status: status ?? (prev?.inMeeting ? 'busy' : 'online'),
    })
    try {
      if (status === null) await this.pushOwner('status:clear', {})
      else await this.pushOwner('status:set', { status })
    } catch (err) {
      if (prev) this.applyInfo(prev)
      this.emit('error', err)
      throw err
    }
  }

  clearStatus(): Promise<void> { return this.setStatus(null) }

  async setCustomMessage(message: string | null): Promise<void> {
    if (typeof message === 'string' && message.length > 80) return
    const prev = this.store.get(this.userId)
    this.applyInfo({ ...(prev ?? aggregate(this.userId, [])), customMessage: message || null })
    try {
      await this.pushOwner('custom_message:set', { message })
    } catch (err) {
      if (prev) this.applyInfo(prev)
      this.emit('error', err)
      throw err
    }
  }

  private pushOwner(event: string, payload: Record<string, unknown>): Promise<unknown> {
    const owner = this.channels.get(this.userId)
    if (!owner) return Promise.reject(new Error('owner channel not joined — call sdk.init() first'))
    return new Promise((resolve, reject) => {
      owner.channel.push(event, payload)
        .receive('ok', (resp: unknown) => resolve(resp))
        .receive('error', (err: unknown) => reject(err))
        .receive('timeout', () => reject(new Error(`${event} timed out`)))
    })
  }

  /** Bind `fn` to the owner `user:<id>` channel as soon as it exists (immediately
   *  if already joined). Used by CallsModule to share the owner channel. */
  onOwnerChannel(fn: (ch: Channel) => void): void {
    this.ownerBinders.add(fn)
    const owner = this.channels.get(this.userId)
    if (owner) this.safeBind(fn, owner.channel)
  }

  /** Push an event on the owner `user:<id>` channel (used by CallsModule). */
  pushOnOwner(event: string, payload: Record<string, unknown>): Promise<unknown> {
    return this.pushOwner(event, payload)
  }

  private safeBind(fn: (ch: Channel) => void, channel: Channel): void {
    try { fn(channel) } catch (e) { this.logger?.('error', 'owner-channel binder threw', e) }
  }

  // ── Idle → away (browser only, opt-in) ──
  startIdleDetection(): void {
    if (this.idleTimer || typeof window === 'undefined') return
    const ACTIVITY = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'focus'] as const
    const threshold = this.idleThresholdMs

    const bump = () => {
      this.lastActivity = Date.now()
      if (!this.autoSetAway) return
      const me = this.store.get(this.userId)
      if (me && me.manualStatus === 'away') {
        this.autoSetAway = false
        void this.setStatus(null).catch(() => {})
      } else {
        this.autoSetAway = false
      }
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') this.lastActivity = Date.now() - threshold
      else bump()
    }

    ACTIVITY.forEach((ev) => window.addEventListener(ev, bump, { passive: true }))
    document.addEventListener('visibilitychange', onVisibility)

    this.idleTimer = setInterval(() => {
      if (Date.now() - this.lastActivity < threshold) return
      if (this.autoSetAway) return
      const me = this.store.get(this.userId)
      if (!me || me.manualStatus != null) return
      this.autoSetAway = true
      void this.setStatus('away').catch(() => { this.autoSetAway = false })
    }, 30_000)

    this.idleTeardown = () => {
      ACTIVITY.forEach((ev) => window.removeEventListener(ev, bump))
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }

  stopIdleDetection(): void {
    if (this.idleTimer) { clearInterval(this.idleTimer); this.idleTimer = null }
    this.idleTeardown?.()
    this.idleTeardown = null
    this.autoSetAway = false
  }

  // ── Snapshots ──
  get(userId: string): PresenceInfo { return this.store.get(userId) ?? aggregate(userId, []) }
  self(): PresenceInfo { return this.get(this.userId) }
  getAll(): Record<string, PresenceInfo> { return Object.fromEntries(this.store) }
  seed(rows: PresenceInfo[]): void { for (const r of rows) this.applyInfo(r) }

  // ── Events ──
  on(event: PresenceEvent, handler: Handler): () => void {
    let set = this.listeners.get(event)
    if (!set) { set = new Set(); this.listeners.set(event, set) }
    set.add(handler)
    return () => set!.delete(handler)
  }

  private emit(event: PresenceEvent, ...args: any[]): void {
    this.listeners.get(event)?.forEach((h) => { try { h(...args) } catch (e) { this.logger?.('error', 'presence listener threw', e) } })
  }

  private applyInfo(info: PresenceInfo): void {
    const prev = this.store.get(info.userId)
    if (prev &&
        prev.status === info.status &&
        prev.manualStatus === info.manualStatus &&
        prev.customMessage === info.customMessage &&
        prev.inMeeting === info.inMeeting &&
        prev.lastSeenAt === info.lastSeenAt) return
    this.store.set(info.userId, info)
    this.emit('change', info.userId, info)
    if (info.userId === this.userId) this.emit('self', info)
  }

  teardown(): void {
    this.stopIdleDetection()
    for (const { channel } of this.channels.values()) { try { channel.leave() } catch {} }
    this.channels.clear()
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 7 — Push module (Firebase Cloud Messaging, web)
 * ════════════════════════════════════════════════════════════════════════════
 *
 *  `firebase` is loaded lazily via dynamic import so it's only required when a
 *  consumer actually opts into push (passes `firebase` + `vapidKey`). All entry
 *  points are best-effort and never throw — push must not break the app.
 */

/** A notification the user CLICKED, normalised so the host handles one shape.
 *  Mirrors the Dart SDK's `PushOpen`. */
export interface PushOpen {
  /** Raw FCM `data`, for keys this interface doesn't promote. */
  data: Record<string, string>
  /** Chat thread to open — present on `chat.message` pushes. */
  conversationId?: string
  messageId?: string
  /** Push category, e.g. `chat.message`. */
  type?: string
}

function toPushOpen(raw: Record<string, unknown>): PushOpen {
  const data: Record<string, string> = {}
  for (const [k, v] of Object.entries(raw ?? {})) {
    if (v !== null && v !== undefined) data[k] = String(v)
  }
  return {
    data,
    conversationId: data.conversationId,
    messageId: data.messageId,
    type: data.type,
  }
}

/** Last Firebase sender (`projectId:vapidKey`) this browser subscribed to, so a
 *  change of project or VAPID key can be detected — see `dropStaleSubscription`. */
const PUSH_SENDER_KEY = 'instavc_push_sender'

export class PushModule {
  private app: any = null
  private messaging: any = null
  private foregroundBound = false
  private cfg: InstavcSdkConfig
  private tokens: SessionTokenProvider
  private openHandlers = new Set<(open: PushOpen) => void>()
  private swBound = false

  /** Held when a click arrives before anyone subscribed (the page is still
   *  mounting). Flushed to the first handler so a click is never dropped. */
  private bufferedOpen: PushOpen | null = null

  constructor(cfg: InstavcSdkConfig, tokens?: SessionTokenProvider) {
    this.cfg = cfg
    this.tokens = tokens ?? new SessionTokenProvider(cfg)
  }

  /**
   * Subscribe to notification CLICKS. Returns an unsubscribe function.
   *
   * On web the click is handled by the service worker, which runs outside the
   * page — so it posts the payload back and this surfaces it. Clicks that
   * OPEN a new tab can't arrive this way (no page existed yet): the SW falls
   * back to a URL, and reading that is the host's job, since only the host
   * knows its own routes.
   */
  onOpened(handler: (open: PushOpen) => void): () => void {
    this.openHandlers.add(handler)
    const pending = this.bufferedOpen
    if (pending) {
      this.bufferedOpen = null
      queueMicrotask(() => handler(pending))
    }
    this.bindNotificationClicks()
    return () => { this.openHandlers.delete(handler) }
  }

  /** Listen for the service worker's click messages. Idempotent; a no-op where
   *  service workers don't exist (SSR, unsupported browsers). */
  bindNotificationClicks(): void {
    if (this.swBound) return
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    this.swBound = true
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const msg = event.data as { type?: string; data?: Record<string, unknown> } | null
      if (!msg || msg.type !== 'notification-open') return
      this.emitOpen(toPushOpen(msg.data ?? {}))
    })
  }

  private emitOpen(open: PushOpen): void {
    if (this.openHandlers.size === 0) { this.bufferedOpen = open; return }
    this.openHandlers.forEach((h) => { try { h(open) } catch { /* host error */ } })
  }

  private enabled(): boolean { return !!(this.cfg.firebase && this.cfg.vapidKey) }

  /**
   * Drop a `PushSubscription` minted by a DIFFERENT Firebase project / VAPID key.
   *
   * `getToken` will NOT do this for us. firebase/messaging's `getPushSubscription`
   * returns whatever subscription the registration already has and never calls
   * `subscribe()` again — it does not so much as compare the key. So after a
   * project or VAPID rotation the browser keeps a subscription bound to the OLD
   * `applicationServerKey` while FCM signs the send with the NEW project's key,
   * and the push service rejects every message.
   *
   * It cannot self-heal: the SDK's IndexedDB token record is keyed by `appId`,
   * which changes in the same rotation, so the lookup misses and the one branch
   * that would compare `vapidKey` is never reached — and that branch does not
   * unsubscribe either. A `PushSubscription` survives reload, tab close and
   * re-login, so only an explicit `unsubscribe()` clears it.
   *
   * The failure is silent in both directions: `register()` either swallows the
   * throw and returns false, or reports an apparently healthy token upstream. So
   * this runs before the first `getToken` of every new (projectId, vapidKey)
   * pair. The marker is written straight after the unsubscribe, not after a
   * successful `getToken`, so that a later token failure retries the *token* and
   * does not unsubscribe the fresh subscription it just made.
   *
   * The orphaned server-side row needs no cleanup: once the subscription is gone
   * its token returns `registration-token-not-registered`, which is already in
   * the prune list.
   */
  private async dropStaleSubscription(swReg: ServiceWorkerRegistration): Promise<void> {
    const sender = `${this.cfg.firebase?.projectId ?? ''}:${this.cfg.vapidKey ?? ''}`
    let seen: string | null
    try {
      seen = localStorage.getItem(PUSH_SENDER_KEY)
    } catch {
      return // storage blocked (private window): rotation can't be tracked, so don't guess
    }
    if (seen === sender) return
    try {
      const stale = await swReg.pushManager.getSubscription()
      if (stale) {
        await stale.unsubscribe()
        this.cfg.logger?.('info', 'push: dropped a subscription from a previous Firebase sender')
      }
    } catch (err) {
      // Leave the marker unwritten so the next register() tries again.
      this.cfg.logger?.('warn', 'push: could not drop the previous subscription', err)
      return
    }
    try { localStorage.setItem(PUSH_SENDER_KEY, sender) } catch { /* best effort */ }
  }

  private authToken(): Promise<string> {
    return this.tokens.get()
  }

  private async ensureMessaging(): Promise<any | null> {
    if (!this.enabled()) return null
    try {
      const [{ initializeApp, getApps }, messagingMod] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ])
      const { getMessaging, isSupported } = messagingMod
      if (!(await isSupported().catch(() => false))) return null
      this.app = getApps().length ? getApps()[0] : initializeApp(this.cfg.firebase as FirebaseWebConfig)
      if (!this.messaging) this.messaging = getMessaging(this.app)
      return this.messaging
    } catch (err) {
      this.cfg.logger?.('warn', 'push: firebase not available', err)
      return null
    }
  }

  /** Request permission, get an FCM token, and register it with the SDK server. */
  async register(): Promise<boolean> {
    if (!this.enabled()) return false
    try {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !('Notification' in window)) return false
      const m = await this.ensureMessaging()
      if (!m) return false

      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return false

      const { getToken, onMessage } = await import('firebase/messaging')
      const swReg = await navigator.serviceWorker.register(this.cfg.swPath || '/firebase-messaging-sw.js')
      await this.dropStaleSubscription(swReg)
      const token = await getToken(m, { vapidKey: this.cfg.vapidKey, serviceWorkerRegistration: swReg })
      if (!token) return false

      await this.postToken(token)

      if (!this.foregroundBound) {
        this.foregroundBound = true
        onMessage(m, async (payload: any) => {
          const n = payload.notification
          if (!n || Notification.permission !== 'granted') return
          const data = (payload.data ?? {}) as Record<string, string>
          try {
            const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.ready)
            await reg.showNotification(n.title ?? 'New message', {
              body: n.body, icon: '/favicon.svg', data, tag: data.conversationId,
            })
          } catch {
            new Notification(n.title ?? 'New message', { body: n.body, icon: '/favicon.svg' })
          }
        })
      }
      return true
    } catch (err) {
      this.cfg.logger?.('warn', 'push: registration failed', err)
      return false
    }
  }

  /** Delete the FCM token locally + on the server (call on logout). */
  async unregister(): Promise<void> {
    if (!this.enabled()) return
    try {
      const m = await this.ensureMessaging()
      if (!m) return
      const { getToken, deleteToken } = await import('firebase/messaging')
      const token = await getToken(m, { vapidKey: this.cfg.vapidKey }).catch(() => '')
      if (token) {
        const auth = await this.authToken()
        await fetch(`${this.cfg.sdkApiUrl ?? DEFAULT_SDK_API_URL}/api/v1/push/tokens/${encodeURIComponent(token)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${auth}` },
        }).catch(() => {})
        await deleteToken(m).catch(() => {})
      }
    } catch { /* best-effort */ }
  }

  /** Register the token server-side.
   *
   *  `projectId` is the Firebase project this token was minted by. Only Admin
   *  credentials for that same project can push to it, and one SDK server
   *  serves host apps built on different projects — so the server routes each
   *  token to the matching sender, and cannot deliver to an untagged one
   *  except via its default. Always report it; the config is right here. */
  private async postToken(token: string): Promise<void> {
    const auth = await this.authToken()
    await fetch(`${this.cfg.sdkApiUrl ?? DEFAULT_SDK_API_URL}/api/v1/push/tokens`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify({
        token,
        platform: 'web',
        deviceId: this.cfg.deviceId,
        projectId: this.cfg.firebase?.projectId,
      }),
    }).catch(() => {})
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 7b — Contacts (contact identity / contactPresenceId)
 * ════════════════════════════════════════════════════════════════════════════ */

/** The current user's stored, scope-scoped contact/identity record. */
export interface SelfContact {
  /** The user's identity within this scope — keys all presence/chat topics,
   *  shared across all of the org's apps (per-org boundary). */
  contactPresenceId: string
  scopeId: string
  /** Child-company scope within the scope; null when none. App-provided. */
  subId: string | null
  /** Company/brand name — app-provided, always populated (defaults to 'instavc'). */
  contactOrgName: string
  email: string
  /** SecureID user id (IdP sub) — cross-app-stable identity. */
  secureUserId: string | null
  displayName: string | null
  avatarUrl: string | null
  termsAcceptedAt: string | null
  termsVersion: string | null
  linkedUserIds: string[]
  createdAt: string
}

export interface RegisterSelfInput {
  /** Version string of the terms the user accepted (for audit). */
  termsVersion?: string
  avatarUrl?: string
  displayName?: string
  /** Company/brand name — app-provided, mandatory server-side (defaults to
   *  'instavc'). Set this to your company for a white-labelled integration. */
  contactOrgName?: string
  /** Sub-organisation scope within the scope — app-provided, optional.
   *  Sets the per-sub-org identity when present. */
  subId?: string
}

/**
 * Contacts / contact-identity (SDK-owned, REST on the SDK server).
 *
 * When the chat tab opens, call `lookupSelf()`; if `exists === false`, show the
 * terms gate and, on accept, call `registerSelf()`. Both are scoped to the
 * scope + email carried in the app JWT — two apps of the same scope resolve to
 * ONE contact and the SAME `contactPresenceId`.
 */
export class ContactsModule {
  private cfg: InstavcSdkConfig
  private tokens: SessionTokenProvider
  constructor(cfg: InstavcSdkConfig, tokens?: SessionTokenProvider) {
    this.cfg = cfg
    this.tokens = tokens ?? new SessionTokenProvider(cfg)
  }

  private async authToken(): Promise<string> {
    return this.tokens.get()
  }
  private base(): string { return this.cfg.sdkApiUrl ?? DEFAULT_SDK_API_URL }

  /** Does the current user already have a contact/identity record in this scope? */
  async lookupSelf(
    opts: { subId?: string } = {},
  ): Promise<{ exists: boolean; contact: SelfContact | null }> {
    const auth = await this.authToken()
    // Per-sub-org consent check: when the app operates in a sub-org, ask
    // whether the user opted in FOR THAT sub-org. `exists:false` → show the
    // T&C gate and `registerSelf({ subId })` to create a separate contact.
    // Falls back to the SDK config's `subId` when not passed.
    const subId = opts.subId ?? this.cfg.subId
    const q = subId ? `?subId=${encodeURIComponent(subId)}` : ''
    const res = await fetch(`${this.base()}/api/v1/contacts/self${q}`, {
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`contacts.lookupSelf failed: ${res.status}`)
    return res.json()
  }

  /** Accept T&C + register (or resolve) the current user's contact identity. */
  async registerSelf(input: RegisterSelfInput = {}): Promise<SelfContact> {
    const auth = await this.authToken()
    // Apply SDK-config defaults (set once) for company + sub-org so the app
    // doesn't repeat them on every call. Explicit args still win.
    const payload = {
      acceptedTerms: true,
      contactOrgName: input.contactOrgName ?? this.cfg.contactOrgName,
      subId: input.subId ?? this.cfg.subId,
      ...input,
    }
    const res = await fetch(`${this.base()}/api/v1/contacts/self`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(`contacts.registerSelf failed: ${res.status}`)
    const data = (await res.json()) as { contact: SelfContact }
    return data.contact
  }

  /**
   * Discover contacts to chat with. **Partial** match on name/email — `'ali'` →
   * Alice, Ali, Alia… `q` empty = browse everyone in reach. One method for
   * browse + lookup.
   *
   * `scope` sets the reach:
   *   - `'subId'`   — your scope AND your sub-org (default)
   *   - `'scopeId'` — your whole scope, any sub-org
   *   - `'global'`  — the whole platform, any scope
   *
   * `limit` defaults to 50. Rows carry `contactOrgName` (disambiguate the same
   * person across companies) + `avatarUrl`. `id` = contactPresenceId (start a
   * DM with it).
   */
  async search(
    q = '',
    scope: 'subId' | 'scopeId' | 'global' = 'subId',
    limit = 50,
  ): Promise<SearchContact[]> {
    const auth = await this.authToken()
    const qs = new URLSearchParams({ scope, limit: String(limit) })
    if (q) qs.set('q', q)
    const res = await fetch(`${this.base()}/api/v1/contacts/search?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`contacts.search failed: ${res.status}`)
    const data = (await res.json()) as { contacts: SearchContact[] }
    return data.contacts
  }

  /**
   * Block a contact. Idempotent — blocking someone already blocked is a no-op.
   *
   * The block is enforced SERVER-SIDE, so you do not have to filter anything
   * yourself. From this point, for that user:
   *   - their DMs to you are dropped — silently, so they can't tell they're
   *     blocked. Their app shows the message as sent; you never receive it, and
   *     it is never stored.
   *   - their calls never ring you.
   *   - neither of you sees the other's presence.
   *
   * Scope is **1:1**. A group you both belong to is unaffected — you'll still
   * see each other's messages there.
   *
   * `userId` is a `contactPresenceId`.
   */
  async block(userId: string): Promise<void> {
    const auth = await this.authToken()
    const res = await fetch(`${this.base()}/api/v1/contacts/${encodeURIComponent(userId)}/block`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`contacts.block failed: ${res.status}`)
  }

  /**
   * Unblock a contact. Idempotent.
   *
   * Does NOT deliver anything they sent while blocked — those messages were
   * dropped at the door, never stored, so there is no backlog to release. Only
   * what they send from now on arrives.
   */
  async unblock(userId: string): Promise<void> {
    const auth = await this.authToken()
    const res = await fetch(`${this.base()}/api/v1/contacts/${encodeURIComponent(userId)}/block`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`contacts.unblock failed: ${res.status}`)
  }

  /** The caller's chat privacy settings. */
  async getPrivacy(): Promise<PrivacySettings> {
    const auth = await this.authToken()
    const res = await fetch(`${this.base()}/api/v1/contacts/self/privacy`, {
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`contacts.getPrivacy failed: ${res.status}`)
    return res.json() as Promise<PrivacySettings>
  }

  /**
   * Update either privacy flag. Returns the settings now IN FORCE, so render
   * from the response rather than from what you asked for.
   *
   * `readReceipts: false` — the server stops publishing your read cursor, so
   * nobody sees your blue ticks (live or on reload). Delivered ✓✓ still works,
   * and group receipts are unaffected. **Reciprocity is yours to apply**: hide
   * other people's blue ticks in your UI while this is off, matching what users
   * expect from WhatsApp.
   *
   * `showStatus: false` — others can no longer watch your presence at all; they
   * resolve you to offline. Deliberately NOT reciprocal — you still see theirs.
   */
  async setPrivacy(patch: Partial<PrivacySettings>): Promise<PrivacySettings> {
    const auth = await this.authToken()
    const res = await fetch(`${this.base()}/api/v1/contacts/self/privacy`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw new Error(`contacts.setPrivacy failed: ${res.status}`)
    return res.json() as Promise<PrivacySettings>
  }

  /**
   * Account-level chat preferences — theme and the default chat background.
   *
   * These follow the user across devices, which is the point of storing them
   * here rather than in local storage. Per-DEVICE settings (auto-download, for
   * instance) deliberately stay client-side.
   */
  async getPreferences(): Promise<ChatPreferences> {
    const auth = await this.authToken()
    const res = await fetch(`${this.base()}/api/v1/contacts/self/preferences`, {
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`contacts.getPreferences failed: ${res.status}`)
    return res.json() as Promise<ChatPreferences>
  }

  /** Update theme and/or default background. Returns what is now in force. */
  async setPreferences(patch: Partial<ChatPreferences>): Promise<ChatPreferences> {
    const auth = await this.authToken()
    const res = await fetch(`${this.base()}/api/v1/contacts/self/preferences`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw new Error(`contacts.setPreferences failed: ${res.status}`)
    return res.json() as Promise<ChatPreferences>
  }

  /** Everyone the caller has blocked, newest first. */
  async listBlocked(): Promise<BlockedContact[]> {
    const auth = await this.authToken()
    const res = await fetch(`${this.base()}/api/v1/contacts/blocked`, {
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`contacts.listBlocked failed: ${res.status}`)
    const data = (await res.json()) as { blocked: BlockedContact[] }
    return data.blocked
  }
}

/**
 * Starred messages — the caller's PRIVATE bookmarks.
 *
 * REST-only (no socket): a star is personal state, never broadcast, and the
 * other side of the conversation is never told. Two people can star the same
 * message with no knowledge of each other.
 */
export class StarsModule {
  private cfg: InstavcSdkConfig
  private tokens: SessionTokenProvider
  constructor(cfg: InstavcSdkConfig, tokens?: SessionTokenProvider) {
    this.cfg = cfg
    this.tokens = tokens ?? new SessionTokenProvider(cfg)
  }

  private base(): string { return this.cfg.sdkApiUrl ?? DEFAULT_SDK_API_URL }

  private async req<T>(path: string, init: RequestInit = {}): Promise<T> {
    const auth = await this.tokens.get()
    const res = await fetch(`${this.base()}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}`, ...(init.headers ?? {}) },
    })
    if (!res.ok) throw new Error(`stars ${init.method ?? 'GET'} ${path} failed: ${res.status}`)
    return res.json() as Promise<T>
  }

  /** Star a message. Idempotent. `messageId` is the public id you already hold. */
  async star(messageId: string): Promise<void> {
    await this.req(`/api/v1/messages/${encodeURIComponent(messageId)}/star`, { method: 'POST' })
  }

  /** Remove a star. Idempotent. */
  async unstar(messageId: string): Promise<void> {
    await this.req(`/api/v1/messages/${encodeURIComponent(messageId)}/star`, { method: 'DELETE' })
  }

  /** My starred messages, newest-starred first. Cursor = previous page's `nextCursor`. */
  async list(opts: { limit?: number; before?: string } = {}): Promise<StarredPage> {
    const qs = new URLSearchParams()
    if (opts.limit != null) qs.set('limit', String(opts.limit))
    if (opts.before) qs.set('before', opts.before)
    const q = qs.toString()
    return this.req<StarredPage>(`/api/v1/messages/starred${q ? `?${q}` : ''}`)
  }

  /**
   * Which of these message ids have I starred? Use it when a thread loads so
   * each bubble can show its star without pulling the whole starred list.
   */
  async starredIn(messageIds: string[]): Promise<string[]> {
    if (messageIds.length === 0) return []
    const data = await this.req<{ messageIds: string[] }>('/api/v1/messages/starred/lookup', {
      method: 'POST',
      body: JSON.stringify({ messageIds }),
    })
    return data.messageIds
  }
}

/** A starred message, hydrated enough to render the list and jump to the thread. */
export interface StarredMessage {
  messageId: string
  conversationId: string | null
  /** Group title, or the DM peer's name. Null when unresolvable. */
  conversationName: string | null
  senderId: string | null
  senderName: string | null
  body: string | null
  /** First attachment's kind, so an attachment-only star isn't a blank row. */
  attachmentKind: string | null
  /**
   * The attachments themselves. Optional because a server older than this
   * field sends only `attachmentKind`; render from the kind when absent.
   */
  attachments?: Array<ChatAttachment & { url: string | null }>
  /** When the message was sent. Null if its row hasn't landed yet. */
  createdAt: string | null
  /** When it was starred — the list's sort key. */
  starredAt: string
}

export interface StarredPage {
  items: StarredMessage[]
  nextCursor: string | null
  hasMore: boolean
}

/** How the app should render — `system` follows the OS setting. */
export type ThemeMode = 'system' | 'light' | 'dark'

/** Account-level chat preferences, synced across the user's devices. */
export interface ChatPreferences {
  theme: ThemeMode
  /** Default background for threads with no override. Opaque to the SDK — it's
   *  whatever token your app maps to an asset, gradient or colour. Null = your
   *  built-in default. */
  chatBackground: string | null
  /** Accent colour for chat bubbles and avatars. Opaque to the SDK — a hex
   *  string or preset token. Null = your app's default accent. */
  chatColor: string | null
  /**
   * Silence PUSH notifications for all group threads.
   *
   * Push ONLY: messages still arrive, unread counts still climb, threads still
   * update live. Enforced server-side at the push fan-out, so you don't filter
   * anything yourself. A single thread can also be muted on its own via
   * `conversations.patch(id, { muted: true })`; either is enough.
   *
   * Calls are never affected — muting messages doesn't drop someone's call.
   */
  muteGroups: boolean
  /** The same, for 1:1 threads. */
  muteDirect: boolean
}

/** The caller's chat privacy toggles. */
export interface PrivacySettings {
  /** False ⇒ your read cursor is never published; nobody sees your blue ticks.
   *  Delivered (grey) ticks and group receipts are unaffected. */
  readReceipts: boolean
  /** False ⇒ others cannot watch your presence; they resolve you to offline.
   *  You still see theirs (not reciprocal). */
  showStatus: boolean
}

/** A row from [ContactsModule.listBlocked]. */
export interface BlockedContact {
  /** contactPresenceId of the blocked user — pass to `unblock()`. */
  userId: string
  name: string | null
  avatarUrl: string | null
  /** ISO-8601 timestamp of when the block was created. */
  blockedAt: string
}

/* ── Standalone identity helpers (resolve `contactPresenceId` before the SDK) ──
 *
 *  The chat/presence identity (`contactPresenceId`) is baked into `InstavcSdk` at
 *  construction, but you only learn it AFTER registering. Rather than build a
 *  throwaway SDK just for that, call these two socket-free helpers first, then
 *  construct the ONE real `InstavcSdk` with `presenceId` set. They need only
 *  `getAppToken` (the SDK exchanges it internally, same as everything else).
 */

/** Does the caller already have a contact in this scope/sub-org? */
export function lookupSelfContact(opts: {
  getAppToken: () => string | Promise<string>
  sdkApiUrl?: string
  subId?: string
}): Promise<{ exists: boolean; contact: SelfContact | null }> {
  return new ContactsModule({
    userId: '', getAppToken: opts.getAppToken, sdkApiUrl: opts.sdkApiUrl, subId: opts.subId,
  }).lookupSelf({ subId: opts.subId })
}

/** Accept T&C + create/resolve the caller's contact → `SelfContact` (idempotent). */
export function registerSelfContact(
  opts: { getAppToken: () => string | Promise<string>; sdkApiUrl?: string } & RegisterSelfInput,
): Promise<SelfContact> {
  const { getAppToken, sdkApiUrl, ...input } = opts
  return new ContactsModule({
    userId: '', getAppToken, sdkApiUrl, subId: input.subId,
  }).registerSelf(input)
}

/** A contact from [ContactsModule.search]. `contactOrgName` disambiguates the same
 *  person across companies. */
export interface SearchContact {
  /** contactPresenceId — start a DM with this id. */
  id: string
  name: string | null
  email: string
  avatarUrl: string | null
  contactOrgName: string
  subId: string | null
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 7b-2 — Conversations module (control-plane REST)
 * ════════════════════════════════════════════════════════════════════════════
 *
 *  DM/group lifecycle over sdk-server REST: create, list, membership, rename,
 *  avatar, leave. The realtime `chat` module operates on an ALREADY-created
 *  `conversation:<id>` — this is how you obtain that id. Uses the same shared
 *  SessionTokenProvider for auth. All member ids are `contactPresenceId`s.
 * ════════════════════════════════════════════════════════════════════════════ */

export type ConversationKind = 'dm' | 'group'

export interface ConversationParticipant {
  userId: string
  name: string | null
  avatarUrl: string | null
  isAdmin: boolean
  archived: boolean
  muted: boolean
  pinned: boolean
  lastReadAt: string | null
  lastDeliveredAt: string | null
  joinedAt: string
}

export interface Conversation {
  id: string
  kind: ConversationKind
  title: string | null
  avatarUrl: string | null
  createdBy: string | null
  dmUserA: string | null
  dmUserB: string | null
  lastMessageAt: string | null
  /**
   * Newest message in the thread — enough to render a chat-list preview line
   * WITHOUT loading each thread first. Present on `list()` and `get()`; null when
   * the conversation has never been messaged.
   *
   * Use this for the list tile. `lastMessageAt` alone gives you a timestamp with
   * no text, which is why a client with a cold cache (fresh device, private
   * window, cleared storage) used to show blank previews.
   */
  lastMessage?: LastMessagePreview | null
  /** YOUR background override for this thread, or null to follow your account
   *  default (`contacts.getPreferences().chatBackground`). Per-participant —
   *  the other member has their own and never sees yours. */
  background?: string | null
  createdAt: string
  updatedAt: string
  /** Hydrated on `get()` (detail); may be absent on `list()`. */
  participants?: ConversationParticipant[]
}

/** Preview of a conversation's newest message (a subset of `ChatMessage`). */
export interface LastMessagePreview {
  /** Same id space as `ChatMessage.id` — dedupe against an already-loaded thread. */
  id: string
  senderId: string | null
  type: string
  body: string | null
  createdAt: string
  deletedAt: string | null
  /** First attachment's kind, so an attachment-only message can preview as
   *  "Photo"/"Document" instead of empty text. Null when there are none. */
  attachmentKind: string | null
}

export interface ConversationPage {
  items: Conversation[]
  nextCursor: string | null
  hasMore: boolean
}

export class ConversationsModule {
  private cfg: InstavcSdkConfig
  private tokens: SessionTokenProvider
  constructor(cfg: InstavcSdkConfig, tokens?: SessionTokenProvider) {
    this.cfg = cfg
    this.tokens = tokens ?? new SessionTokenProvider(cfg)
  }

  private base(): string { return this.cfg.sdkApiUrl ?? DEFAULT_SDK_API_URL }

  private async req<T>(path: string, init: RequestInit = {}, noBody = false): Promise<T> {
    const auth = await this.tokens.get()
    const res = await fetch(`${this.base()}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}`, ...(init.headers ?? {}) },
    })
    if (!res.ok) throw new Error(`conversations ${init.method ?? 'GET'} ${path} failed: ${res.status}`)
    if (noBody || res.status === 204) return undefined as T
    return res.json() as Promise<T>
  }

  /**
   * Set (or clear, with null) MY background for one thread — WhatsApp's per-chat
   * wallpaper. Per-participant: it never changes what the other member sees.
   * Falls back to the account default when cleared.
   */
  async setBackground(id: string, background: string | null): Promise<string | null> {
    const data = await this.req<{ background: string | null }>(
      `/api/v1/conversations/${encodeURIComponent(id)}/background`,
      { method: 'PUT', body: JSON.stringify({ background }) },
    )
    return data.background
  }

  /** Thread list, newest first. */
  async list(opts: { limit?: number; before?: string } = {}): Promise<ConversationPage> {
    const q = new URLSearchParams()
    if (opts.limit) q.set('limit', String(opts.limit))
    if (opts.before) q.set('before', opts.before)
    const qs = q.toString()
    return this.req<ConversationPage>(`/api/v1/conversations${qs ? `?${qs}` : ''}`)
  }

  /** Detail + hydrated participants. */
  async get(conversationId: string): Promise<Conversation> {
    const { conversation } = await this.req<{ conversation: Conversation }>(`/api/v1/conversations/${conversationId}`)
    return conversation
  }

  /** Open (or return the existing) DM with a contact. Idempotent per user-pair. */
  async createDm(peerUserId: string): Promise<Conversation> {
    const { conversation } = await this.req<{ conversation: Conversation }>(`/api/v1/conversations`, {
      method: 'POST',
      body: JSON.stringify({ kind: 'dm', memberIds: [peerUserId] }),
    })
    return conversation
  }

  /** Create a group (caller becomes admin). `avatarUrl` from an uploaded image. */
  async createGroup(input: { title: string; memberIds: string[]; avatarUrl?: string | null }): Promise<Conversation> {
    const { conversation } = await this.req<{ conversation: Conversation }>(`/api/v1/conversations`, {
      method: 'POST',
      body: JSON.stringify({ kind: 'group', ...input }),
    })
    return conversation
  }

  /** Add members (admin). Returns the full participant list. */
  async addMembers(conversationId: string, userIds: string[]): Promise<ConversationParticipant[]> {
    const { participants } = await this.req<{ participants: ConversationParticipant[] }>(
      `/api/v1/conversations/${conversationId}/members`,
      { method: 'POST', body: JSON.stringify({ userIds }) },
    )
    return participants
  }

  /** Remove a member (admin) or remove yourself. */
  async removeMember(conversationId: string, userId: string): Promise<void> {
    await this.req<void>(`/api/v1/conversations/${conversationId}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' }, true)
  }

  /** Promote / demote a member to admin (admin, group only). Returns participants. */
  async setMemberAdmin(conversationId: string, userId: string, isAdmin: boolean): Promise<ConversationParticipant[]> {
    const { participants } = await this.req<{ participants: ConversationParticipant[] }>(
      `/api/v1/conversations/${conversationId}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ isAdmin }) },
    )
    return participants
  }

  /** Rename a group (admin). */
  rename(conversationId: string, title: string): Promise<Conversation> {
    return this.patch(conversationId, { title })
  }

  /** Set/clear the group photo (admin). */
  setAvatar(conversationId: string, avatarUrl: string | null): Promise<Conversation> {
    return this.patch(conversationId, { avatarUrl })
  }

  /** General patch: group `title`/`avatarUrl` (admin) + your own flags. */
  async patch(
    conversationId: string,
    input: { title?: string; avatarUrl?: string | null; muted?: boolean; pinned?: boolean; archived?: boolean },
  ): Promise<Conversation> {
    const { conversation } = await this.req<{ conversation: Conversation }>(`/api/v1/conversations/${conversationId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    return conversation
  }

  /**
   * Clear chat — empty the thread FOR YOU while KEEPING it in your list.
   *
   * One-sided: the floor is stored on your own participant row, so the other
   * members keep their full history and are never told. It applies to every
   * server read (REST history, the channel's `history:fetch` and `sync:fetch`),
   * which is what makes a clear survive a reload rather than being a repaint.
   *
   * `keepStarred` is WhatsApp's checkbox and defaults to true: your starred
   * messages stay visible in the thread. Pass false and they go with the rest,
   * along with the stars themselves. `keptCount` reports how many survived —
   * re-fetch the thread when it is non-zero to show them.
   *
   * A local cache (IndexedDB, an in-memory store) must be purged separately —
   * the server agreeing they are gone does not empty a cache it cannot see.
   */
  async clear(
    conversationId: string,
    opts: { keepStarred?: boolean } = {},
  ): Promise<{ clearedAt: string; keptCount: number }> {
    return this.req<{ clearedAt: string; keptCount: number }>(
      `/api/v1/conversations/${conversationId}/clear`,
      { method: 'POST', body: JSON.stringify({ keepStarred: opts.keepStarred ?? true }) },
    )
  }

  /**
   * Delete chat — dismiss the thread from your list AND clear its history for
   * you, in one step. Also one-sided, and also permanent for you.
   *
   * The conversation itself lives on: a new message resurfaces it carrying only
   * what arrived after the delete, and re-creating the DM (`create`) returns the
   * same thread rather than a duplicate. So the other party's chat continues
   * unbroken while yours starts empty — which is the behaviour to expect, not a
   * failed delete.
   */
  async delete(conversationId: string, opts: { keepStarred?: boolean } = {}): Promise<void> {
    await this.req<void>(
      `/api/v1/conversations/${conversationId}`,
      { method: 'DELETE', body: JSON.stringify({ keepStarred: opts.keepStarred ?? true }) },
      true,
    )
  }

  /** @deprecated Use {@link delete}, which this now forwards to unchanged. */
  async leave(conversationId: string): Promise<void> {
    await this.delete(conversationId)
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 7b2 — Broadcasts module (one-to-many lists)
 * ════════════════════════════════════════════════════════════════════════════
 *
 *  A broadcast list is WhatsApp's: owned by ONE user, holding N recipients, and
 *  INVISIBLE to those recipients. `send()` drops the message into each
 *  recipient's DM with the owner as an ordinary message — they cannot tell it
 *  was a broadcast, and their reply is a normal private DM back to the owner.
 *
 *  The fan-out runs SERVER-SIDE: one REST call ensures a DM per recipient and
 *  delivers into each, so a 256-recipient list costs the app one request rather
 *  than 256 channel joins, and can't be stranded half-sent by a backgrounded tab.
 *
 *  The list also has its own thread (`broadcast.id` IS a conversation id) holding
 *  the owner's copy of everything they sent. To render it, join it like any other
 *  conversation: `sdk.chat.join(broadcast.id)` + `sdk.chat.fetchHistory(...)`.
 */

/** A member of a broadcast list. `userId` is a contactPresenceId. */
export interface BroadcastRecipient {
  userId: string
  name: string | null
  avatarUrl: string | null
  addedAt: string
}

export interface Broadcast {
  /** Also the conversation id of the owner's sent-copy thread. */
  id: string
  name: string | null
  avatarUrl: string | null
  ownerId: string | null
  recipientCount: number
  /** Preview: body of the last message sent to the list (null if never used). */
  lastMessage: string | null
  lastMessageAt: string | null
  createdAt: string
  updatedAt: string
  recipients: BroadcastRecipient[]
}

/** One recipient's receipt for a broadcast message. Null = not yet. */
export interface BroadcastReceipt {
  userId: string
  name: string | null
  avatarUrl: string | null
  deliveredAt: string | null
  readAt: string | null
}

/** Aggregate (and optionally per-recipient) receipts for one broadcast message.
 *  `total` is how many recipients it actually reached — the ledger size, which
 *  can be smaller than the list if a delivery failed. */
export interface BroadcastMessageReceipts {
  messageId: string
  total: number
  delivered: number
  read: number
  /** Only from `receiptFor()` — the Message-info breakdown. */
  recipients?: BroadcastReceipt[]
}

/** Outcome of one `send()`. Per-recipient failures are REPORTED, not thrown —
 *  one unreachable DM never sinks the rest of the fan-out. */
export interface BroadcastSendResult {
  /** Id of the owner's copy in the broadcast thread. */
  messageId: string
  delivered: number
  failed: { userId: string; reason: string }[]
  /** Where each delivered copy landed — your DM with that recipient and the
   *  message's id in it. The live `message:new` for a copy carries no broadcast
   *  flag (the recipient hears the same event), so mark these yourself. A DM
   *  this send created reaches you through nothing else: add it to your chat
   *  list from here. Absent from servers that predate it. */
  copies?: BroadcastCopy[]
}

/** One recipient's copy of a broadcast, in your DM with them. */
export interface BroadcastCopy {
  userId: string
  /** Your DM with this recipient — possibly created by this very send. */
  conversationId: string
  messageId: string
  /** The copy's timestamp, as stored. Absent from servers that predate it. */
  createdAt?: string
}

export class BroadcastsModule {
  private cfg: InstavcSdkConfig
  private tokens: SessionTokenProvider
  constructor(cfg: InstavcSdkConfig, tokens?: SessionTokenProvider) {
    this.cfg = cfg
    this.tokens = tokens ?? new SessionTokenProvider(cfg)
  }

  private base(): string { return this.cfg.sdkApiUrl ?? DEFAULT_SDK_API_URL }

  private async req<T>(path: string, init: RequestInit = {}, noBody = false): Promise<T> {
    const auth = await this.tokens.get()
    const res = await fetch(`${this.base()}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}`, ...(init.headers ?? {}) },
    })
    if (!res.ok) throw new Error(`broadcasts ${init.method ?? 'GET'} ${path} failed: ${res.status}`)
    if (noBody || res.status === 204) return undefined as T
    return res.json() as Promise<T>
  }

  /** Your lists, most-recently-used first. Recipients come hydrated. */
  async list(): Promise<Broadcast[]> {
    const { items } = await this.req<{ items: Broadcast[] }>('/api/v1/broadcasts')
    return items
  }

  /** One list + its recipients. */
  async get(broadcastId: string): Promise<Broadcast> {
    const { broadcast } = await this.req<{ broadcast: Broadcast }>(`/api/v1/broadcasts/${broadcastId}`)
    return broadcast
  }

  /** Create a list (1–256 recipients, by contactPresenceId). */
  async create(input: { name: string; recipientIds: string[]; avatarUrl?: string | null }): Promise<Broadcast> {
    const { broadcast } = await this.req<{ broadcast: Broadcast }>('/api/v1/broadcasts', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    return broadcast
  }

  /** Rename the list. */
  rename(broadcastId: string, name: string): Promise<Broadcast> {
    return this.patch(broadcastId, { name })
  }

  /** Set/clear the list photo (`avatarUrl` from an uploaded attachment). */
  setAvatar(broadcastId: string, avatarUrl: string | null): Promise<Broadcast> {
    return this.patch(broadcastId, { avatarUrl })
  }

  async patch(broadcastId: string, input: { name?: string; avatarUrl?: string | null }): Promise<Broadcast> {
    const { broadcast } = await this.req<{ broadcast: Broadcast }>(`/api/v1/broadcasts/${broadcastId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    return broadcast
  }

  /** Add recipients. Returns the full recipient list. */
  async addRecipients(broadcastId: string, userIds: string[]): Promise<BroadcastRecipient[]> {
    const { recipients } = await this.req<{ recipients: BroadcastRecipient[] }>(
      `/api/v1/broadcasts/${broadcastId}/recipients`,
      { method: 'POST', body: JSON.stringify({ userIds }) },
    )
    return recipients
  }

  /** Drop a recipient — their DM history with you is untouched, they just stop
   *  receiving future sends. Returns the remaining recipients. */
  async removeRecipient(broadcastId: string, userId: string): Promise<BroadcastRecipient[]> {
    const { recipients } = await this.req<{ recipients: BroadcastRecipient[] }>(
      `/api/v1/broadcasts/${broadcastId}/recipients/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    )
    return recipients
  }

  /** Delete the list. Recipients keep every message already delivered. */
  async delete(broadcastId: string): Promise<void> {
    await this.req<void>(`/api/v1/broadcasts/${broadcastId}`, { method: 'DELETE' }, true)
  }

  /**
   * Empty the owner's copy of the list's thread, KEEPING the list and its
   * recipients.
   *
   * Distinct from {@link delete}, which destroys the list outright — the owner
   * is its only participant, so there is no "leave" to fall back on. Clear is
   * therefore the only way to drop the history without losing the recipients.
   *
   * What recipients already received is untouched: their copies live in their
   * own DMs with the owner, as ordinary messages they were never told came from
   * a list.
   */
  async clear(
    broadcastId: string,
    opts: { keepStarred?: boolean } = {},
  ): Promise<{ clearedAt: string; keptCount: number }> {
    return this.req<{ clearedAt: string; keptCount: number }>(
      `/api/v1/broadcasts/${broadcastId}/clear`,
      { method: 'POST', body: JSON.stringify({ keepStarred: opts.keepStarred ?? true }) },
    )
  }

  /**
   * Send to everyone on the list. Each recipient gets it as a normal DM from
   * you; you get a copy in the list's own thread (`broadcastId`).
   *
   * Resolves once the server has fanned out — check `failed` for the recipients
   * it could not reach.
   */
  async send(
    broadcastId: string,
    input: { body: string; messageType?: string; attachments?: unknown[] },
  ): Promise<BroadcastSendResult> {
    return this.req<BroadcastSendResult>(`/api/v1/broadcasts/${broadcastId}/send`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  /**
   * Aggregate receipts for a batch of the list's messages — one round-trip for
   * a whole thread of bubbles. Feed each result to [statusFor] to render ticks.
   *
   * A recipient's read/delivered cursor on their DM is what's counted, so this
   * needs no cooperation from their client beyond normal chat behaviour.
   */
  async receipts(broadcastId: string, messageIds: string[]): Promise<BroadcastMessageReceipts[]> {
    if (messageIds.length === 0) return []
    const q = new URLSearchParams({ messageIds: messageIds.join(',') })
    const { receipts } = await this.req<{ receipts: BroadcastMessageReceipts[] }>(
      `/api/v1/broadcasts/${broadcastId}/receipts?${q.toString()}`,
    )
    return receipts
  }

  /** Receipts for ONE message, with the per-recipient breakdown — the
   *  "Message info" sheet (who has it, who has read it). */
  async receiptFor(broadcastId: string, messageId: string): Promise<BroadcastMessageReceipts> {
    const { receipt } = await this.req<{ receipt: BroadcastMessageReceipts }>(
      `/api/v1/broadcasts/${broadcastId}/messages/${messageId}/receipts`,
    )
    return receipt
  }

  /**
   * Tick state for a broadcast message, WhatsApp's rule: ✓✓ only once EVERY
   * recipient has it, blue only once EVERY recipient has read it.
   *
   * Mirrors `chat.statusFor` — which can't be used on a list, because a list
   * thread has exactly one participant (you), so it would read `sent` forever.
   * Messages sent before receipts shipped have no ledger (`total === 0`) and
   * stay at `sent`.
   */
  statusFor(receipts: BroadcastMessageReceipts | undefined): MessageStatus {
    if (!receipts || receipts.total === 0) return 'sent'
    if (receipts.read >= receipts.total) return 'read'
    if (receipts.delivered >= receipts.total) return 'delivered'
    return 'sent'
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 7c — Calls module (1:1 ring handshake)
 * ════════════════════════════════════════════════════════════════════════════
 *
 *  The SDK does exactly TWO things for calls: (1) ring a user's CONNECTED
 *  devices so any one can accept — THIS module — and (2) push their offline
 *  devices via FCM (server-side, follow-up). Everything else — WebRTC/media,
 *  room join, ring UI, ringtone, call history — is the APP's job. The invite
 *  carries an OPAQUE `payload` (room id / meeting link / caller info) the SDK
 *  never reads; the app puts whatever it needs to start the real call in there.
 *
 *  Call events ride the SAME owner `user:<id>` channel presence uses (via
 *  PresenceModule.onOwnerChannel), so there's no extra topic/subscription. The
 *  server fans a ring to every connected device and, when one device answers,
 *  cancels the ring on the caller's siblings — the "answered elsewhere" echo on
 *  the answering device itself is dropped via `answeredByDeviceId`.
 */

export type CallType = 'audio' | 'video'
export type CallDirection = 'outgoing' | 'incoming'
/** ringing → `accepted` (call live, app owns media) → `ended` (either side hung
 *  up), or a ring-time terminal (`rejected`/`cancelled`/`missed`). */
export type CallState = 'ringing' | 'accepted' | 'rejected' | 'cancelled' | 'missed' | 'ended'
export type CallEvent = 'incoming' | 'stateChange' | 'error'

export interface CallSession {
  callId: string
  direction: CallDirection
  /** Callee (outgoing) or caller (incoming) — a contactPresenceId. */
  peerId: string
  callType: CallType
  /** Opaque app blob carried through the ring (room id / link / caller info). */
  payload: Record<string, unknown>
  state: CallState
  /** Caller's display name (incoming only), stamped by the server. */
  callerName?: string
  /** Terminal reason for rejected / cancelled / missed. */
  reason?: string
}

/** An incoming ring surfaced to the app, with accept/reject bound to this call. */
export interface IncomingCall extends CallSession {
  accept(): Promise<void>
  reject(reason?: string): Promise<void>
}

/** A PAST call from [CallsModule.history], as THIS user sees it: `peer*` is the
 *  other party and `direction` is relative to you. Written server-side by
 *  signaling when the call ends — one row per call. */
export interface CallRecord {
  id: string
  /** The other party's contactPresenceId — call them back with `invite(peerId)`. */
  peerId: string
  peerName: string | null
  peerAvatarUrl: string | null
  direction: CallDirection
  callType: CallType
  /** True when the call was never answered (missed / rejected / cancelled). */
  missed: boolean
  durationMs: number
  createdAt: string
}

export interface CallHistoryPage {
  items: CallRecord[]
  nextCursor: string | null
  hasMore: boolean
}

function newCallId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  } catch { /* fall through */ }
  return `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * The owner `user:<id>` channel, abstracted so CallsModule can ride EITHER the
 * SDK's own PresenceModule (which implements this) OR a host app's existing
 * owner channel — apps that already own `user:<id>` (e.g. the host app's
 * `useOwnUserChannel`) can't let the SDK's PresenceModule join the same topic
 * (Phoenix closes the duplicate), so they build a CallsModule over an adapter
 * around their channel instead.
 */
export interface OwnerChannelHost {
  /** Invoke `fn` with the owner channel once it exists (immediately if joined). */
  onOwnerChannel(fn: (ch: Channel) => void): void
  /** Push an event on the owner channel. */
  pushOnOwner(event: string, payload: Record<string, unknown>): Promise<unknown>
}

export class CallsModule {
  private host: OwnerChannelHost
  private deviceId?: string
  private logger?: Logger
  private cfg?: InstavcSdkConfig
  private tokens?: SessionTokenProvider

  private channel: Channel | null = null
  private calls = new Map<string, CallSession>()
  private listeners = new Map<CallEvent, Set<Handler>>()

  constructor(
    host: OwnerChannelHost,
    deviceId?: string,
    logger?: Logger,
    cfg?: InstavcSdkConfig,
    tokens?: SessionTokenProvider,
  ) {
    this.host = host
    this.deviceId = deviceId
    this.logger = logger
    this.cfg = cfg
    this.tokens = tokens
  }

  /**
   * **Past calls** — this user's call log (both directions), newest first.
   * Cursor-paginated: pass the returned `nextCursor` as `before` for the next
   * page. Records are written server-side by signaling when a call ends, so the
   * log is complete even if the app was closed. Each row is from YOUR point of
   * view: `peer*` is the other party and `direction` is relative to you.
   */
  async history(opts: { limit?: number; before?: string } = {}): Promise<CallHistoryPage> {
    if (!this.tokens) {
      throw new Error('InstavcSdk: calls.history() requires the SDK-owned module (use sdk.calls)')
    }
    const auth = await this.tokens.get()
    const qs = new URLSearchParams()
    if (opts.limit != null) qs.set('limit', String(opts.limit))
    if (opts.before) qs.set('before', opts.before)
    const base = this.cfg?.sdkApiUrl ?? DEFAULT_SDK_API_URL
    const res = await fetch(`${base}/api/v1/calls${qs.toString() ? `?${qs.toString()}` : ''}`, {
      headers: { Authorization: `Bearer ${auth}` },
    })
    if (!res.ok) throw new Error(`calls.history failed: ${res.status}`)
    return res.json() as Promise<CallHistoryPage>
  }

  /** Bind call events onto the owner `user:<id>` channel. Called by the facade
   *  during init(); safe before or after the socket connects. */
  start(): void {
    this.host.onOwnerChannel((ch) => this.bind(ch))
  }

  private bind(channel: Channel): void {
    if (this.channel === channel) return
    this.channel = channel
    channel.on('call:incoming', (p: Record<string, unknown>) => this.onIncoming(p))
    channel.on('call:accepted', (p: Record<string, unknown>) => this.onTerminal(p, 'accepted'))
    channel.on('call:rejected', (p: Record<string, unknown>) => this.onTerminal(p, 'rejected'))
    channel.on('call:missed', (p: Record<string, unknown>) => this.onTerminal(p, 'missed'))
    channel.on('call:cancelled', (p: Record<string, unknown>) => this.onCancelled(p))
    channel.on('call:ended', (p: Record<string, unknown>) => this.onEnded(p))
  }

  // ── Caller API ──
  /** Ring `calleeId`'s connected devices. Returns the callId (client-generated).
   *  `payload` is opaque — put the room/meeting info the app needs on accept. */
  async invite(
    calleeId: string,
    opts: { callType?: CallType; payload?: Record<string, unknown>; timeoutMs?: number } = {},
  ): Promise<string> {
    const callId = newCallId()
    const callType: CallType = opts.callType === 'audio' ? 'audio' : 'video'
    const payload = opts.payload ?? {}
    const session: CallSession = { callId, direction: 'outgoing', peerId: calleeId, callType, payload, state: 'ringing' }
    this.calls.set(callId, session)
    this.emit('stateChange', session)
    try {
      const msg: Record<string, unknown> = { callId, calleeId, callType, payload }
      if (opts.timeoutMs != null) msg.timeoutMs = opts.timeoutMs
      await this.host.pushOnOwner('call:invite', msg)
    } catch (err) {
      this.finalize(callId, 'cancelled', 'invite_failed')
      this.emit('error', err)
      throw err
    }
    return callId
  }

  /** Give up an outgoing ring before it's answered. */
  cancel(callId: string): Promise<void> {
    const s = this.calls.get(callId)
    if (!s || s.direction !== 'outgoing') return Promise.resolve()
    this.finalize(callId, 'cancelled', 'caller_cancelled')
    return this.push('call:cancel', { callId, calleeId: s.peerId })
  }

  // ── Callee API ──
  /** Accept an incoming ring. The call is RETAINED (state `accepted`) so either
   *  side can later `end()` it; the app owns media/room-join meanwhile. */
  accept(callId: string): Promise<void> {
    const s = this.calls.get(callId)
    if (!s || s.direction !== 'incoming') return Promise.resolve()
    this.markAccepted(callId)
    return this.push('call:accept', { callId, callerId: s.peerId })
  }

  /** Decline an incoming ring. */
  reject(callId: string, reason = 'declined'): Promise<void> {
    const s = this.calls.get(callId)
    if (!s || s.direction !== 'incoming') return Promise.resolve()
    this.finalize(callId, 'rejected', reason)
    return this.push('call:reject', { callId, callerId: s.peerId, reason })
  }

  /** Hang up an ANSWERED call — notifies the peer so their screen tears down too.
   *  (Ring-time give-ups use `cancel`/`reject`.) */
  end(callId: string): Promise<void> {
    const s = this.calls.get(callId)
    if (!s) return Promise.resolve()
    const peerId = s.peerId
    this.finalize(callId, 'ended')
    return this.push('call:end', { callId, peerId })
  }

  // ── Snapshots + events ──
  get(callId: string): CallSession | undefined { return this.calls.get(callId) }
  active(): CallSession[] { return [...this.calls.values()] }

  on(event: CallEvent, handler: Handler): () => void {
    let set = this.listeners.get(event)
    if (!set) { set = new Set(); this.listeners.set(event, set) }
    set.add(handler)
    return () => set!.delete(handler)
  }

  /** Convenience: subscribe to incoming rings only. */
  onIncomingCall(handler: (call: IncomingCall) => void): () => void {
    return this.on('incoming', handler as Handler)
  }

  teardown(): void { this.calls.clear(); this.channel = null; this.listeners.clear() }

  // ── internals ──
  private onIncoming(p: Record<string, unknown>): void {
    const callId = String(p?.callId ?? '')
    if (!callId || this.calls.has(callId)) return
    const session: CallSession = {
      callId,
      direction: 'incoming',
      peerId: String(p?.from ?? ''),
      callType: p?.callType === 'audio' ? 'audio' : 'video',
      payload: p?.payload && typeof p.payload === 'object' ? (p.payload as Record<string, unknown>) : {},
      state: 'ringing',
      callerName: typeof p?.callerName === 'string' ? p.callerName : undefined,
    }
    this.calls.set(callId, session)
    this.emit('incoming', this.asIncoming(session))
    this.emit('stateChange', session)
  }

  private onTerminal(p: Record<string, unknown>, state: 'accepted' | 'rejected' | 'missed'): void {
    const callId = String(p?.callId ?? '')
    if (!this.calls.has(callId)) return
    // Caller side: the callee picked up → RETAIN as accepted (so end() works);
    // rejected/missed are terminal.
    if (state === 'accepted') this.markAccepted(callId)
    else this.finalize(callId, state, typeof p?.reason === 'string' ? p.reason : undefined)
  }

  private onCancelled(p: Record<string, unknown>): void {
    // Ignore the "answered/declined elsewhere" echo on the device that just
    // handled the call itself.
    if (this.deviceId && p?.answeredByDeviceId === this.deviceId) return
    const callId = String(p?.callId ?? '')
    if (!this.calls.has(callId)) return
    this.finalize(callId, 'cancelled', typeof p?.reason === 'string' ? p.reason : undefined)
  }

  /** The peer hung up an answered call. */
  private onEnded(p: Record<string, unknown>): void {
    const callId = String(p?.callId ?? '')
    if (!this.calls.has(callId)) return
    this.finalize(callId, 'ended')
  }

  private asIncoming(s: CallSession): IncomingCall {
    return { ...s, accept: () => this.accept(s.callId), reject: (r?: string) => this.reject(s.callId, r) }
  }

  /** Mark an answered call live but KEEP it (so either side can end() it). */
  private markAccepted(callId: string): void {
    const s = this.calls.get(callId)
    if (!s) return
    const next: CallSession = { ...s, state: 'accepted', reason: undefined }
    this.calls.set(callId, next)
    this.emit('stateChange', next)
  }

  /** Emit the terminal state, then forget the call. */
  private finalize(callId: string, state: CallState, reason?: string): void {
    const s = this.calls.get(callId)
    if (!s) return
    const next: CallSession = { ...s, state, reason }
    this.calls.delete(callId)
    this.emit('stateChange', next)
  }

  private push(event: string, payload: Record<string, unknown>): Promise<void> {
    return this.host.pushOnOwner(event, payload).then(() => undefined).catch((err) => {
      this.emit('error', err)
      this.logger?.('warn', `calls: ${event} failed`, err)
    })
  }

  private emit(event: CallEvent, ...args: unknown[]): void {
    this.listeners.get(event)?.forEach((h) => { try { h(...args) } catch (e) { this.logger?.('error', 'calls listener threw', e) } })
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 *  SECTION 8 — The facade
 * ════════════════════════════════════════════════════════════════════════════ */

export class InstavcSdk {
  readonly chat: ChatModule
  readonly presence: PresenceModule
  readonly push: PushModule
  readonly contacts: ContactsModule
  readonly conversations: ConversationsModule
  /** Private starred-message bookmarks (REST-only). */
  readonly stars: StarsModule
  readonly broadcasts: BroadcastsModule
  readonly calls: CallsModule
  private sm: SocketManager
  private started = false

  constructor(cfg: InstavcSdkConfig) {
    if (!cfg.userId) throw new Error('InstavcSdk: `userId` is required')
    // ONE token provider shared across socket + REST modules: the app token is
    // exchanged for an SDK session token once per lifetime, cached, refreshed.
    const tokens = new SessionTokenProvider(cfg)
    this.sm = new SocketManager(cfg, tokens)
    // Chat + presence act as the per-org identity (contactPresenceId) when set;
    // the socket + meetings keep cfg.userId (secureUserId).
    const chatPresenceId = cfg.presenceId ?? cfg.userId
    this.chat = new ChatModule(this.sm, chatPresenceId, cfg.autoDelivered ?? true, cfg.logger)
    this.presence = new PresenceModule(this.sm, chatPresenceId, cfg.idleThresholdMs ?? 5 * 60 * 1000, cfg.logger)
    this.push = new PushModule(cfg, tokens)
    this.contacts = new ContactsModule(cfg, tokens)
    this.conversations = new ConversationsModule(cfg, tokens)
    this.stars = new StarsModule(cfg, tokens)
    this.broadcasts = new BroadcastsModule(cfg, tokens)
    // Calls ring on the SAME per-org identity presence uses; they share its
    // owner `user:<id>` channel (no extra topic). deviceId disambiguates this
    // device for the "answered elsewhere" multi-device cancel.
    this.calls = new CallsModule(this.presence, cfg.deviceId, cfg.logger, cfg, tokens)
  }

  /** Fires on socket open/close. */
  onConnectionChange(h: (connected: boolean) => void): void {
    this.sm.onOpen(() => h(true))
    this.sm.onClose(() => h(false))
  }

  /**
   * Bring everything up IN SEQUENCE:
   *   1. open (or adopt) the Phoenix socket,
   *   2. start presence on the owner topic (self status becomes writable),
   *   3. register for push (only if `firebase` + `vapidKey` were provided).
   * Idempotent — safe to call once per signed-in user.
   */
  async init(opts: { registerPush?: boolean } = {}): Promise<void> {
    if (this.started) return
    this.started = true
    await this.sm.connect()                 // 1
    this.presence.start()                    // 2
    this.calls.start()                       // 2b — bind call ring onto the owner channel
    // 2c — chat's own owner-topic events (`conversation:activity`). Same hook
    // as the ring: the nudge for a thread the user has not opened arrives on
    // `user:<id>`, not on any conversation topic.
    this.presence.onOwnerChannel((ch) => this.chat.bindOwnerChannel(ch))
    if (opts.registerPush !== false) {       // 3 (no-op unless push configured)
      void this.push.register()
    }
  }

  /** True once the socket is open (or an injected socket was supplied). */
  get connected(): boolean { return this.sm.isReady() }

  /** Direct access to the underlying socket (owned or injected), if any. */
  get socket(): Socket | null { return this.sm.socket }

  /**
   * Tear down: leave all channels, stop idle detection, and disconnect the
   * socket IF the SDK opened it (an injected socket is left untouched).
   * Does NOT unregister push — call `sdk.push.unregister()` explicitly on logout
   * if you want the device to stop receiving notifications.
   */
  destroy(): void {
    this.chat.teardown()
    this.calls.teardown()
    this.presence.teardown()
    this.sm.disconnect()
    this.started = false
  }
}

export default InstavcSdk
